import React, { useState, useMemo, useEffect } from 'react';
import { Doughnut, Bar, Line } from 'react-chartjs-2';
import './lms-analytics.styles.css';
import { STATIC_DATA, LmsAnalyticsData } from './lms-analytics.data';
import { DataSourceModal } from './data-source-modal.component';
import { InfoIcon } from './info-icon.component';
import { donutChartOptions, horizontalBarChartOptions, lineChartOptions } from './chart-utils';
import { getChartPalette, getStatusColors, getToken, useThemeVersion } from '../../theme/chartTheme';
import ApiService from '../../_services/ApiService';
import { AiChat } from './ai-chat.component';
import Config from '../../_config/config';
import { useITeachConfig } from '../../_context/PresetContext';
import AuthenticationService from '../../_services/AuthenticationService';

const colorScale = (p: number) => {
  const h = 120 * (p / 100);
  return `hsl(${h},70%,85%)`;
};

// CSV Export utility
const exportToCSV = (data: any[], filename: string, headers: string[]) => {
  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(header => {
      const value = row[header];
      // Handle values with commas or quotes
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(','))
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface LmsAnalyticsProps {
  data?: LmsAnalyticsData;
  showDataSource?: boolean; // Show badges indicating data source
}

export const LmsAnalytics: React.FC<LmsAnalyticsProps> = ({
  data = STATIC_DATA,
  showDataSource = false
}) => {
  const DATA = data;
  const { config: iteachConfig } = useITeachConfig();

  // Token-resolved chart colors (canvas can't consume var() references);
  // themeVersion bumps on light/dark toggle so the datasets recompute
  const themeVersion = useThemeVersion();
  const chartColors = useMemo(() => getChartPalette(), [themeVersion]);
  const chartStatus = useMemo(() => getStatusColors(), [themeVersion]);

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    source: 'api' | 'calculated' | 'mock';
    records?: any[];
    explanation?: string;
    apiUrl?: string;
  }>({
    isOpen: false,
    title: '',
    source: 'api',
    records: [],
    explanation: '',
    apiUrl: ''
  });

  // Pagination state for At Risk table
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Risk filter state
  const [riskFilter, setRiskFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'approaching'>('all');

  // Risk Intelligence feature box state
  const [riskFeatureBoxExpanded, setRiskFeatureBoxExpanded] = useState(() => {
    const saved = localStorage.getItem('riskFeatureBoxExpanded');
    return saved === null ? true : saved === 'true'; // Default expanded for first-time users
  });

  const toggleRiskFeatureBox = () => {
    const newState = !riskFeatureBoxExpanded;
    setRiskFeatureBoxExpanded(newState);
    localStorage.setItem('riskFeatureBoxExpanded', String(newState));
  };

  // Pagination state for Skills & Gaps tab
  const [competencyGapPage, setCompetencyGapPage] = useState(1);
  const [learnerGapPage, setLearnerGapPage] = useState(1);
  const [learningGainsPage, setLearningGainsPage] = useState(1);
  const skillsPageSize = 25;

  // Widget expand/collapse state
  const [expandedWidgets, setExpandedWidgets] = useState<Set<string>>(new Set());

  // Tab state
  const [activeTab, setActiveTab] = useState<'overview' | 'programs' | 'learners' | 'skills' | 'insights' | 'nudge' | 'engagement' | 'xapi'>('overview');

  // AI Insights state
  const [aiInsights, setAiInsights] = useState<{
    loading: boolean;
    data: {
      predictedOverdue?: number;
      topRiskCompetency?: string;
      recommendedModule?: string;
      additionalInsights?: string[];
    } | null;
    error?: string;
  }>({
    loading: false,
    data: null
  });

  // Nudge Dashboard state
  const [nudgeOverview, setNudgeOverview] = useState<any>(null);
  const [nudgeHistory, setNudgeHistory] = useState<any[]>([]);
  const [nudgeHistoryPage, setNudgeHistoryPage] = useState(1);
  const [nudgeHistoryTotal, setNudgeHistoryTotal] = useState(0);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [nudgeFilters, setNudgeFilters] = useState({
    channel: '',
    startDate: '',
    endDate: '',
    learnerId: ''
  });
  const [nudgeRules, setNudgeRules] = useState<any[]>([]);

  // Manual Nudge state
  const [manualNudgePanel, setManualNudgePanel] = useState<{
    isOpen: boolean;
    learner?: any;
    course?: string;
    enrollmentId?: number;
  }>({ isOpen: false });
  const [manualNudgeForm, setManualNudgeForm] = useState({
    channel: 'email',
    subject: '',
    message: ''
  });
  const [sendingNudge, setSendingNudge] = useState(false);

  // Learner detail modal state
  const [learnerDetail, setLearnerDetail] = useState<any>(null);
  const [loadingLearnerDetail, setLoadingLearnerDetail] = useState(false);

  // Engagement Analytics state
  const [engagementView, setEngagementView] = useState<'learner' | 'resource'>('learner');
  const [learnerEngagementData, setLearnerEngagementData] = useState<any[]>([]);
  const [resourceUsageData, setResourceUsageData] = useState<any[]>([]);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [selectedLearnerDetail, setSelectedLearnerDetail] = useState<any>(null);
  const [selectedResourceDetail, setSelectedResourceDetail] = useState<any>(null);
  const [loadingEngagementDetail, setLoadingEngagementDetail] = useState(false);

  // Infinite scroll pagination state
  const [displayedLearnerCount, setDisplayedLearnerCount] = useState(50);
  const [displayedResourceCount, setDisplayedResourceCount] = useState(50);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // New Rule modal state
  const [newRuleModal, setNewRuleModal] = useState(false);
  const [newRuleForm, setNewRuleForm] = useState({
    name: '',
    description: '',
    category: 'engagement',
    triggerConditions: [{ field: '', operator: '', value: '' }],
    channels: [] as string[],
    priority: 'medium',
    maxPerDay: 1,
    maxPerWeek: 3
  });
  const [creatingRule, setCreatingRule] = useState(false);

  // Fetch learner details including preferences
  const fetchLearnerDetail = async (learnerId: number) => {
    try {
      setLoadingLearnerDetail(true);
      const response = await fetch(`${Config.nodeSiteUrl}v1/nudges/dashboard/learner/${learnerId}`);
      const result = await response.json();
      if (result.success) {
        setLearnerDetail(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch learner details:', error);
    } finally {
      setLoadingLearnerDetail(false);
    }
  };

  // Open manual nudge panel
  const openManualNudgePanel = (learner: string, course: string, enrollmentId?: number, learnerId?: number) => {
    setManualNudgePanel({
      isOpen: true,
      learner: { name: learner, id: learnerId },
      course,
      enrollmentId
    });
    setManualNudgeForm({
      channel: 'email',
      subject: '',
      message: ''
    });
  };

  // Close manual nudge panel
  const closeManualNudgePanel = () => {
    setManualNudgePanel({ isOpen: false });
    setManualNudgeForm({ channel: 'email', subject: '', message: '' });
  };

  // Send manual nudge
  const sendManualNudge = async () => {
    if (!manualNudgeForm.message.trim()) {
      alert('Please enter a message');
      return;
    }
    if (manualNudgeForm.channel === 'email' && !manualNudgeForm.subject.trim()) {
      alert('Please enter a subject for email');
      return;
    }

    try {
      setSendingNudge(true);
      const response = await fetch(`${Config.nodeSiteUrl}v1/nudges/manual/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          learnerId: manualNudgePanel.learner?.id,
          enrollmentId: manualNudgePanel.enrollmentId,
          channel: manualNudgeForm.channel,
          subject: manualNudgeForm.channel === 'email' ? manualNudgeForm.subject : '',
          message: manualNudgeForm.message,
          scheduledAt: null
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Nudge sent successfully!');
        closeManualNudgePanel();
        // Refresh overview if loaded
        if (nudgeOverview) {
          fetchNudgeOverview(30);
        }
        // Refresh history if loaded
        if (nudgeHistory.length > 0) {
          fetchNudgeHistory(1);
        }
      } else {
        alert('❌ Failed to send nudge: ' + (result.error?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error sending nudge:', error);
      alert('❌ Failed to send nudge. Please check console for details.');
    } finally {
      setSendingNudge(false);
    }
  };

  // Fetch Nudge Overview
  const fetchNudgeOverview = async (days: number = 30) => {
    try {
      setNudgeLoading(true);
      const response = await fetch(`${Config.nodeSiteUrl}v1/nudges/dashboard/overview?days=${days}`);
      const result = await response.json();
      if (result.success) {
        setNudgeOverview(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch nudge overview:', error);
    } finally {
      setNudgeLoading(false);
    }
  };

  // Fetch Nudge Rules
  const fetchNudgeRules = async () => {
    try {
      const response = await fetch(`${Config.nodeSiteUrl}v1/nudges/rules`);
      const result = await response.json();
      if (result.success) {
        setNudgeRules(result.rules);
      }
    } catch (error) {
      console.error('Failed to fetch nudge rules:', error);
    }
  };

  // Delete nudge rule
  const deleteNudgeRule = async (ruleId: number, ruleName: string) => {
    // First confirmation
    const firstConfirm = window.confirm(
      `⚠️ WARNING: You are about to delete the nudge rule:\n\n"${ruleName}"\n\nThis will permanently remove the rule and it will no longer trigger nudges.\n\nAre you sure you want to continue?`
    );

    if (!firstConfirm) {
      return;
    }

    // Second confirmation for safety
    const secondConfirm = window.confirm(
      `⚠️ FINAL CONFIRMATION\n\nDeleting "${ruleName}" cannot be undone.\n\nClick OK to permanently delete this rule, or Cancel to keep it.`
    );

    if (!secondConfirm) {
      return;
    }

    try {
      const response = await fetch(`${Config.nodeSiteUrl}v1/nudges/rules/${ruleId}`, {
        method: 'DELETE'
      });
      const result = await response.json();

      if (result.success) {
        alert('✅ Rule deleted successfully');
        // Refresh rules list
        fetchNudgeRules();
      } else {
        alert('❌ Failed to delete rule: ' + (result.error?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('❌ Failed to delete rule');
    }
  };

  // Create new nudge rule
  const createNudgeRule = async () => {
    try {
      setCreatingRule(true);

      // Build trigger condition object from array
      const triggerCondition: any = {};
      newRuleForm.triggerConditions.forEach(tc => {
        if (tc.field && tc.operator && tc.value) {
          triggerCondition[tc.field] = { [tc.operator]: parseFloat(tc.value) || tc.value };
        }
      });

      const payload = {
        name: newRuleForm.name,
        description: newRuleForm.description,
        category: newRuleForm.category,
        triggerCondition,
        targetSegment: {},
        channels: newRuleForm.channels,
        priority: newRuleForm.priority,
        frequencyCap: {
          maxPerDay: newRuleForm.maxPerDay,
          maxPerWeek: newRuleForm.maxPerWeek
        }
      };

      const response = await fetch(`${Config.nodeSiteUrl}v1/nudges/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ Nudge rule created successfully!');
        setNewRuleModal(false);
        // Reset form
        setNewRuleForm({
          name: '',
          description: '',
          category: 'engagement',
          triggerConditions: [{ field: '', operator: '', value: '' }],
          channels: [],
          priority: 'medium',
          maxPerDay: 1,
          maxPerWeek: 3
        });
        // Refresh rules list
        fetchNudgeRules();
      } else {
        alert('❌ Failed to create rule: ' + (result.error?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating rule:', error);
      alert('❌ Failed to create rule. Please check console for details.');
    } finally {
      setCreatingRule(false);
    }
  };

  // Fetch Nudge History
  const fetchNudgeHistory = async (page: number = 1) => {
    try {
      setNudgeLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '25'
      });

      if (nudgeFilters.channel) params.append('channel', nudgeFilters.channel);
      if (nudgeFilters.startDate) params.append('startDate', nudgeFilters.startDate);
      if (nudgeFilters.endDate) params.append('endDate', nudgeFilters.endDate);
      if (nudgeFilters.learnerId) params.append('learnerId', nudgeFilters.learnerId);

      const response = await fetch(`${Config.nodeSiteUrl}v1/nudges/dashboard/history?${params}`);
      const result = await response.json();

      if (result.success) {
        setNudgeHistory(result.data.history);
        setNudgeHistoryPage(result.data.pagination.page);
        setNudgeHistoryTotal(result.data.pagination.total);
      }
    } catch (error) {
      console.error('Failed to fetch nudge history:', error);
    } finally {
      setNudgeLoading(false);
    }
  };

  // Fetch AI Insights
  const fetchAiInsights = async () => {
    setAiInsights({ loading: true, data: null });

    try {
      // Check if user is logged in
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser || currentUser === 'undefined') {
        throw new Error('Authentication required. Please login to use AI insights.');
      }

      // Send SUMMARIZED data to avoid token limits
      const rawDataPayload = {
        metrics: {
          totalLearners: DATA.learners,
          overdueCount: DATA.overdue,
          atRiskCount: DATA.risks.length,
          programCount: DATA.programs,
          totalEnrollments: DATA.rawRecords?.allEnrollments?.length || 0,
          totalLessonPlans: DATA.rawRecords?.lessonPlans?.length || 0,
          completionRates: DATA.completionRates,
          programHealth: DATA.programHealth
        },
        competencies: DATA.competencies?.slice(0, 10) || [],
        risksSample: DATA.risks?.slice(0, 10).map(r => ({
          learner: r.learner,
          course: r.course,
          progress: r.progress,
          expected: r.expected,
          riskCategory: r.riskCategory
        })) || [],
        enrollmentSample: DATA.rawRecords?.allEnrollments?.slice(0, 5).map(e => ({
          learnerName: e.learnerName,
          courseName: e.courseName,
          status: e.status,
          progress: e.progress
        })) || []
      };

      const prompt = `You are an AI assistant analyzing learning management system (LMS) data.

I'm providing you with complete raw data from our LMS APIs:

${JSON.stringify(rawDataPayload, null, 2)}

Based on this data, provide actionable insights:

1. Predict the number of overdue learners in the next 30 days (analyze trends)
2. Identify the top at-risk competency (based on low completion rates, high overdue counts)
3. Recommend a specific module for catch-up (based on which courses have most struggling learners)
4. Provide 2-3 additional actionable insights (patterns, trends, or recommendations)

Return your response ONLY as a valid JSON object with this exact structure:
{
  "predictedOverdue": <number>,
  "topRiskCompetency": "<competency name>",
  "recommendedModule": "<module name>",
  "additionalInsights": ["<insight 1>", "<insight 2>", "<insight 3>"]
}`;

      const response = await ApiService.generateLearningAnalytics({ prompt });

      console.log('Full API Response:', response);
      console.log('Response data:', response?.data);

      // Check various possible response structures
      let responseData = response?.data?.data || response?.data?.response || response?.data;

      console.log('Extracted response data:', responseData);

      // Check if response is valid
      if (!responseData || responseData === 'undefined' || responseData === undefined) {
        throw new Error(`Unable to connect to AI service at ${Config.nodeSiteUrl}. Please try again later.`);
      }

      // Clean up markdown formatting if present (```json ... ```)
      if (typeof responseData === 'string') {
        responseData = responseData
          .replace(/```json\s*/g, '')
          .replace(/```\s*/g, '')
          .trim();
      }

      // Try to parse AI response as JSON
      try {
        const parsedData = typeof responseData === 'string'
          ? JSON.parse(responseData)
          : responseData;
        setAiInsights({ loading: false, data: parsedData });
      } catch (parseError) {
        console.error('Failed to parse AI response as JSON:', parseError);
        console.error('Response was:', responseData);
        throw new Error('Unable to process AI response. Please try again.');
      }
    } catch (error: any) {
      console.error('Error fetching AI insights:', error);
      setAiInsights({
        loading: false,
        data: null,
        error: error.message || 'Failed to load AI insights. Please try again later or contact support.'
      });
    }
  };

  const toggleWidget = (widgetId: string) => {
    setExpandedWidgets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(widgetId)) {
        newSet.delete(widgetId);
      } else {
        newSet.add(widgetId);
      }
      return newSet;
    });
  };

  const openModal = (
    title: string,
    source: 'api' | 'calculated' | 'mock',
    records?: any[],
    explanation?: string,
    apiUrl?: string
  ) => {
    setModalState({ isOpen: true, title, source, records, explanation, apiUrl });
  };

  const closeModal = () => {
    setModalState({ ...modalState, isOpen: false });
  };

  // Widget wrapper component
  const Widget: React.FC<{
    id: string;
    title: React.ReactNode;
    children: React.ReactNode;
    style?: React.CSSProperties;
    defaultExpanded?: boolean;
  }> = ({ id, title, children, style = {}, defaultExpanded = false }) => {
    const isExpanded = expandedWidgets.has(id);

    return (
      <div
        className="card"
        style={{
          ...style,
          transition: 'all 0.3s ease',
          ...(isExpanded ? {
            position: 'fixed',
            top: '20px',
            left: '20px',
            right: '20px',
            bottom: '20px',
            zIndex: 1000,
            maxWidth: 'none',
            maxHeight: 'none',
            overflow: 'auto',
          } : {})
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: isExpanded ? '16px' : '12px'
        }}>
          <div style={{ flex: 1 }}>{title}</div>
          <button
            onClick={() => toggleWidget(id)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '4px 8px',
              color: 'var(--text-muted)',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-primary)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
            title={isExpanded ? 'Minimize' : 'Maximize'}
          >
            {isExpanded ? '⊖' : '⊕'}
          </button>
        </div>
        {children}
      </div>
    );
  };

  // Badge component to show data source
  const DataSourceBadge: React.FC<{
    source: 'api' | 'calculated' | 'mock';
    title?: string;
    records?: any[];
    explanation?: string;
    apiUrl?: string;
  }> = ({ source, title = 'Data Source', records, explanation, apiUrl }) => {
    if (!showDataSource) return null;

    const colors = {
      api: { bg: '#dcfce7', color: 'var(--status-success-text)', text: '✓ API' },
      calculated: { bg: '#dbeafe', color: 'var(--brand-primary-active)', text: '≈ Calculated' },
      mock: { bg: '#fef3c7', color: 'var(--status-warning-text)', text: '⚠ Mock' }
    };

    const style = colors[source];
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          openModal(title, source, records, explanation, apiUrl);
        }}
        style={{
          background: style.bg,
          color: style.color,
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 600,
          marginLeft: '6px',
          cursor: 'pointer',
          transition: 'transform 0.1s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        {style.text}
      </span>
    );
  };

  // Chart.js data objects - memoized to prevent unnecessary re-renders
  const learningStatusData = useMemo(() => ({
    labels: ['Completed', 'In Progress', 'Not Started', 'Paused'],
    datasets: [{
      data: DATA.learningDonut,
      backgroundColor: [chartStatus.success, chartStatus.info, chartStatus.warning, chartStatus.danger],
      borderWidth: 0,
    }]
  }), [DATA.learningDonut, chartStatus]);

  const programStatusData = useMemo(() => ({
    labels: ['Completed', 'In Progress', 'Not Started'],
    datasets: [{
      data: DATA.programDonut,
      backgroundColor: [chartStatus.success, chartStatus.info, chartStatus.warning],
      borderWidth: 0,
    }]
  }), [DATA.programDonut, chartStatus]);

  const completionRatesData = useMemo(() => ({
    labels: DATA.completionRates.labels,
    datasets: [{
      label: 'Completion Rate',
      data: DATA.completionRates.values,
      backgroundColor: chartColors[0],
    }]
  }), [DATA.completionRates.labels, DATA.completionRates.values, chartColors]);

  // Funnel chart - simulated with horizontal bar chart
  const trainingData = useMemo(() => ({
    labels: DATA.training.labels,
    datasets: [{
      label: 'Learner Count',
      data: DATA.training.values,
      backgroundColor: [chartColors[2], chartColors[4], chartStatus.success],
      borderRadius: 4,
    }]
  }), [DATA.training.labels, DATA.training.values, chartColors, chartStatus]);

  const funnelChartOptions = useMemo(() => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function(context: any) {
            const total = DATA.training.values[0]; // Enrolled is 100%
            const percentage = total > 0 ? Math.round((context.parsed.x / total) * 100) : 0;
            return `${context.parsed.x} learners (${percentage}% of enrolled)`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return value;
          }
        }
      },
      y: {
        ticks: {
          font: {
            size: 13,
            weight: 'bold' as const
          }
        }
      }
    }
  }), [DATA.training.values]);

  const enrollmentsData = useMemo(() => ({
    labels: DATA.enrollmentsMonthly.labels,
    datasets: [{
      label: 'Enrollments',
      data: DATA.enrollmentsMonthly.values,
      borderColor: chartColors[0],
      backgroundColor: `rgba(${getToken('--brand-primary-rgb')}, 0.1)`,
      tension: 0.3,
    }]
  }), [DATA.enrollmentsMonthly.labels, DATA.enrollmentsMonthly.values, chartColors]);

  const programHealthData = useMemo(() => ({
    labels: DATA.programHealth.labels,
    datasets: [{
      label: 'Health Index',
      data: DATA.programHealth.values,
      backgroundColor: DATA.programHealth.values.map((score) => {
        // Color code based on score ranges to match legend
        // Green: Top performer (70+)
        // Blue: Good performer (50-69)
        // Red: Needs attention (<50)
        if (score >= 70) {
          return chartStatus.success; // Green
        } else if (score >= 50) {
          return chartStatus.info; // Blue
        } else {
          return chartStatus.danger; // Red
        }
      }),
    }]
  }), [DATA.programHealth.labels, DATA.programHealth.values, DATA.programHealth.isTopPerformer, chartStatus]);

  const prePostData = useMemo(() => ({
    labels: ['Baseline', 'Endline'],
    datasets: [{
      label: 'Test Score',
      data: [DATA.prePost.pre, DATA.prePost.post],
      backgroundColor: chartColors[1],
    }]
  }), [DATA.prePost.pre, DATA.prePost.post, chartColors]);

  const exportCsv = () => {
    const rows = DATA.risks.map((r) => ({
      learner: r.learner,
      course: r.course,
      progress: r.progress,
      expected: r.expected,
      gap: r.expected - r.progress,
      last: r.last,
      flags: r.flags.join('|'),
    }));
    const header = Object.keys(rows[0] || { note: 'no data' });
    const csv = [
      header.join(','),
      ...rows.map((o) => header.map((h) => (o as any)[h]).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lms_risk_export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyJson = () => {
    const txt = JSON.stringify(DATA.risks, null, 2);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(txt).then(() => alert('JSON copied'));
    } else {
      const ta = document.createElement('textarea');
      ta.value = txt;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert('JSON copied');
    }
  };

  // Use AI insights data only (no static fallback for display)
  const predicted = aiInsights.data?.predictedOverdue;
  const topRiskComp = aiInsights.data?.topRiskCompetency;
  const recModule = aiInsights.data?.recommendedModule;

  const effectivenessRows = useMemo(() =>
    [...DATA.effectivenessByCourse]
      .map((r) => ({ ...r, gain: r.post - r.pre }))
      .sort((a, b) => b.gain - a.gain),
    [DATA.effectivenessByCourse]
  );

  // Pagination logic for At Risk table with filtering
  const filteredRisks = riskFilter === 'all'
    ? DATA.risks
    : DATA.risks.filter(r => r.riskCategory === riskFilter);

  const totalRisks = filteredRisks.length;
  const totalPages = Math.ceil(totalRisks / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRisks = filteredRisks.slice(startIndex, endIndex);

  // Risk category counts
  const riskCounts = {
    all: DATA.risks.length,
    critical: DATA.risks.filter(r => r.riskCategory === 'critical').length,
    high: DATA.risks.filter(r => r.riskCategory === 'high').length,
    medium: DATA.risks.filter(r => r.riskCategory === 'medium').length,
    approaching: DATA.risks.filter(r => r.riskCategory === 'approaching').length
  };

  const currentUser = AuthenticationService.currentUserValue;
  const userProfilePic = (currentUser as any)?.photoFile || null;
  const userName = currentUser?.enquiryName || currentUser?.userName || 'User';
  const instituteLogo = iteachConfig.logoUrl || `https://webfront.edusquares.com/${Config.site}/files/logos/logo.png`;

  // Engagement Analytics Functions
  const fetchLearnerEngagement = async (programId?: number) => {
    try {
      setEngagementLoading(true);
      const params = programId ? `?programId=${programId}` : '';
      const token = currentUser ? JSON.parse(localStorage.getItem('currentUser') || '{}').token : '';

      const response = await fetch(`${Config.nodeSiteUrl}v1/analytics/learner-engagement-summary${params}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();

      if (result.success) {
        setLearnerEngagementData(result.data);
      } else {
        console.error('Failed to fetch learner engagement:', result.error);
      }
    } catch (error) {
      console.error('Error fetching learner engagement:', error);
    } finally {
      setEngagementLoading(false);
    }
  };

  const fetchResourceUsage = async (programId?: number) => {
    try {
      setEngagementLoading(true);
      const params = programId ? `?programId=${programId}` : '';
      const token = currentUser ? JSON.parse(localStorage.getItem('currentUser') || '{}').token : '';

      const response = await fetch(`${Config.nodeSiteUrl}v1/analytics/resource-usage-summary${params}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();

      if (result.success) {
        setResourceUsageData(result.data);
      } else {
        console.error('Failed to fetch resource usage:', result.error);
      }
    } catch (error) {
      console.error('Error fetching resource usage:', error);
    } finally {
      setEngagementLoading(false);
    }
  };

  const fetchLearnerActivityDetail = async (userId: number, programId?: number) => {
    try {
      setLoadingEngagementDetail(true);
      const params = programId ? `?programId=${programId}` : '';
      const token = currentUser ? JSON.parse(localStorage.getItem('currentUser') || '{}').token : '';

      const response = await fetch(`${Config.nodeSiteUrl}v1/analytics/learner-activity-detail/${userId}${params}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();

      if (result.success) {
        setSelectedLearnerDetail(result.data);
      } else {
        console.error('Failed to fetch learner detail:', result.error);
      }
    } catch (error) {
      console.error('Error fetching learner detail:', error);
    } finally {
      setLoadingEngagementDetail(false);
    }
  };

  const fetchResourceActivityDetail = async (resourceId: number, programId?: number) => {
    try {
      setLoadingEngagementDetail(true);
      const params = programId ? `?programId=${programId}` : '';
      const token = currentUser ? JSON.parse(localStorage.getItem('currentUser') || '{}').token : '';

      const response = await fetch(`${Config.nodeSiteUrl}v1/analytics/resource-activity-detail/${resourceId}${params}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();

      if (result.success) {
        setSelectedResourceDetail(result.data);
      } else {
        console.error('Failed to fetch resource detail:', result.error);
      }
    } catch (error) {
      console.error('Error fetching resource detail:', error);
    } finally {
      setLoadingEngagementDetail(false);
    }
  };

  // Auto-load nudge data when nudge tab is activated
  useEffect(() => {
    if (activeTab === 'nudge' && !nudgeOverview) {
      fetchNudgeOverview(30);
      fetchNudgeHistory(1);
      fetchNudgeRules();
    }
  }, [activeTab]);

  // Auto-load engagement data when engagement tab is activated
  useEffect(() => {
    if (activeTab === 'engagement') {
      if (engagementView === 'learner') {
        fetchLearnerEngagement();
      } else {
        fetchResourceUsage();
      }
    }
  }, [activeTab, engagementView]);

  return (
    <div className="lms-container">
      <header className="lms-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              background: 'white',
              border: '1px solid var(--border)',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              color: 'var(--text)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-secondary)';
              e.currentTarget.style.transform = 'translateX(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.transform = 'translateX(0)';
            }}
          >
            ← Back
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 600 }}>Learning Momentum</h1>
            <div className="muted tiny">Track performance. Reveal patterns. Transform outcomes.</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => window.location.href = '/executive-flip-dashboard'}
            style={{
              background: 'var(--gradient-brand)',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(99, 102, 241, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(99, 102, 241, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(99, 102, 241, 0.3)';
            }}
            title="View Executive Flip Class Dashboard"
          >
            🎓 Flip Class Executive
          </button>
          <img
            src={instituteLogo}
            alt="Institute Logo"
            style={{
              height: '40px',
              width: 'auto',
              objectFit: 'contain'
            }}
            onError={(e) => {
              // Hide image if it fails to load
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
          {userProfilePic ? (
            <img
              src={userProfilePic}
              alt={userName}
              title={userName}
              style={{
                height: '40px',
                width: '40px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid var(--border-default)'
              }}
              onError={(e) => {
                // Fallback to initials if image fails
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          ) : (
            <div
              style={{
                height: '40px',
                width: '40px',
                borderRadius: '50%',
                background: 'var(--brand-primary)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 600,
                fontSize: '16px',
                border: '2px solid var(--border-default)'
              }}
              title={userName}
            >
              {userName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
      </header>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        borderBottom: '2px solid var(--border-default)',
        paddingBottom: '0'
      }}>
        {[
          { id: 'overview', label: '📊 Overview', icon: '📊', isExternal: false },
          { id: 'programs', label: '🎯 Programs', icon: '🎯', isExternal: false },
          { id: 'learners', label: '👥 Learners', icon: '👥', isExternal: false },
          { id: 'skills', label: '🎓 Skills & Gaps', icon: '🎓', isExternal: false },
          { id: 'insights', label: '🤖 AI Insights', icon: '🤖', isExternal: false },
          { id: 'nudge', label: '🔔 Nudges', icon: '🔔', isExternal: false },
          { id: 'engagement', label: '⚡ Engagement', icon: '⚡', isExternal: false },
          { id: 'xapi', label: '📈 xAPI', icon: '📈', isExternal: true }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              if (tab.isExternal) {
                window.open('https://learninglocker.edusquares.com', '_blank', 'noopener,noreferrer');
              } else {
                setActiveTab(tab.id as any);
              }
            }}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id && !tab.isExternal ? '2px solid #3b82f6' : '2px solid transparent',
              padding: '12px 16px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab.id && !tab.isExternal ? 500 : 400,
              color: activeTab === tab.id && !tab.isExternal ? '#1e293b' : '#64748b',
              transition: 'all 0.15s',
              borderRadius: '0',
              marginBottom: '-2px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              outline: 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.id || tab.isExternal) {
                e.currentTarget.style.color = 'var(--text-strong)';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.id || tab.isExternal) {
                e.currentTarget.style.color = 'var(--text-muted)';
              }
            }}
          >
            {tab.label}
            {tab.isExternal && <span style={{ fontSize: '10px' }}>↗</span>}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <>
          {/* KPIs */}
          <section className="grid g-2">
            <div className="card">
              <div className="muted tiny" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Active Program Cohorts
                <InfoIcon
                  tooltip="Number of active training programs currently running. Each program with different start/end dates is counted separately."
                />
                <DataSourceBadge
                  source="api"
                  title="Active Program Cohorts"
                  records={DATA.rawRecords?.activeProgramRecords}
                  explanation="Count of unique program cohorts/runs (LearningModuleId + ModuleStartDate + ModuleEndDate). Each run of a program with different dates is counted separately."
                />
              </div>
              <div className="kpi">{DATA.programs}</div>
            </div>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <div className="muted tiny" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Unique Learners
                    <InfoIcon
                      tooltip="Total number of individual learners enrolled in programs."
                    />
                    <DataSourceBadge
                      source="api"
                      title="Unique Learners"
                      records={DATA.rawRecords?.uniqueLearnerRecords}
                      explanation="Count of unique UserIds from the API"
                    />
                  </div>
                  <div className="kpi" style={{ fontSize: '24px' }}>{DATA.learners}</div>
                </div>
                <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '8px' }}>
                  <div className="muted tiny" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Learner Records
                    <InfoIcon
                      tooltip="Total number of enrollments. One learner can have multiple enrollments if they're in multiple programs."
                    />
                    <DataSourceBadge
                      source="api"
                      title="Learner Records"
                      records={DATA.rawRecords?.allEnrollments}
                      explanation="Total count of all enrollment records from the API (each enrollment of a learner in a program)"
                    />
                  </div>
                  <div className="kpi" style={{ fontSize: '24px' }}>{DATA.rawRecords?.allEnrollments?.length || 0}</div>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="muted tiny" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Enrollments Needing Attention
                <InfoIcon
                  tooltip="Enrollments that are overdue or have upcoming deadlines. These learners may need support or intervention."
                />
                <DataSourceBadge
                  source="api"
                  title="Enrollments Needing Attention"
                  records={[...(DATA.rawRecords?.overdueRecords || []), ...(DATA.rawRecords?.upcomingDeadlineRecords || [])]}
                  explanation="Combined count of overdue enrollments and enrollments with deadlines in next 7 days"
                />
              </div>
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                <div style={{ flex: 1 }}>
                  <div className="muted tiny" style={{ fontSize: '11px' }}>Overdue</div>
                  <div className="kpi" style={{ fontSize: '28px', color: 'var(--status-danger)' }}>{DATA.overdue}</div>
                </div>
                <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '16px' }}>
                  <div className="muted tiny" style={{ fontSize: '11px' }}>Due in 7 Days</div>
                  <div className="kpi" style={{ fontSize: '28px', color: '#ea580c' }}>{DATA.deadlines}</div>
                </div>
              </div>
            </div>
          </section>

          {/* Enrollment Trends & Upcoming Deadlines */}
          <section className="grid g-3">
            <Widget
              id="training-health"
              title={
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Live Training Health
                      <DataSourceBadge source="calculated" />
                    </strong>
                  </div>
                  <span className="muted tiny">
                    Enrollment Funnel: Enrolled → Started → Completed
                  </span>
                </div>
              }
            >
              <div style={{ height: expandedWidgets.has('training-health') ? '400px' : '200px', transition: 'height 0.3s ease' }}>
                <Bar data={trainingData} options={funnelChartOptions} />
              </div>
              <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
                {DATA.training.values[0] > 0 && (
                  <>
                    <div>Drop-off from Enrolled to Started: {DATA.training.values[0] - DATA.training.values[1]} ({Math.round(((DATA.training.values[0] - DATA.training.values[1]) / DATA.training.values[0]) * 100)}%)</div>
                    <div>Drop-off from Started to Completed: {DATA.training.values[1] - DATA.training.values[2]} ({Math.round(((DATA.training.values[1] - DATA.training.values[2]) / DATA.training.values[1]) * 100)}%)</div>
                    <div style={{ marginTop: '4px', fontWeight: 'bold', color: 'var(--status-success)' }}>Overall completion rate: {Math.round((DATA.training.values[2] / DATA.training.values[0]) * 100)}%</div>
                  </>
                )}
              </div>
            </Widget>
            <Widget
              id="monthly-enrollments"
              title={
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Monthly Enrollments
                      <DataSourceBadge
                        source="api"
                        apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`}
                      />
                    </strong>
                  </div>
                  <span className="muted tiny">Trend</span>
                </div>
              }
            >
              <div style={{ height: expandedWidgets.has('monthly-enrollments') ? '400px' : '220px', transition: 'height 0.3s ease' }}>
                <Line data={enrollmentsData} options={lineChartOptions} />
              </div>
            </Widget>
            <Widget
              id="upcoming-deadlines"
              style={{ overflow: 'auto' }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Upcoming End Dates
                    <DataSourceBadge
                      source="api"
                      apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`}
                    />
                  </strong>
                  <span className="pill">
                    Count: <span>{DATA.deadlinesRows.length}</span>
                  </span>
                </div>
              }
            >
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Course <DataSourceBadge source="api" apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`} /></th>
                    <th style={{ textAlign: 'left' }}>Cohort & Deadline <DataSourceBadge source="api" title="Cohort & Deadline" explanation="Cohort identifier from ModuleStartDate and ModuleEndDate" apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`} /></th>
                    <th className="right">Learners <DataSourceBadge source="api" apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`} /></th>
                  </tr>
                </thead>
                <tbody>
                  {DATA.deadlinesRows.map((r, i) => (
                    <tr key={i}>
                      <td style={{ textAlign: 'left' }}>{r.course}</td>
                      <td style={{ textAlign: 'left' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>👥</span>
                            <span>{r.cohort}</span>
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>📅</span>
                            <span>{r.end}</span>
                          </div>
                        </div>
                      </td>
                      <td className="right">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Widget>
          </section>
        </>
      )}

      {/* PROGRAMS TAB */}
      {activeTab === 'programs' && (
        <>
          {/* Program Intelligence - Health Index & Competency Heatmap */}
          <section className="grid g-2" style={{ marginBottom: '24px' }}>
        <Widget
          id="program-health"
          style={{
            border: '2px solid var(--brand-primary)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
            background: 'linear-gradient(to bottom, var(--surface-card), var(--surface-page))'
          }}
          title={
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  🎯 Program Health Index
                  <InfoIcon tooltip={`Composite health score (0-100) per program:\n• Completion Rate: 40 points\n• On-time Completion: 30 points\n• Engagement Rate: 20 points\n• Retention: 10 points\n\nShowing Top 5 & Bottom 5 programs`} />
                  <DataSourceBadge
                    source="calculated"
                    title="Program Health Index"
                    explanation="Multi-factor composite score:\n• Completion rate (% completed) × 40\n• On-time completion (% finished by deadline) × 30\n• Engagement rate (% who started) × 20\n• Retention (% who didn't drop off) × 10\n\nAggregated at program level (weighted average across all cohorts). Top 5 shown in green/blue, Bottom 5 in orange/red."
                  />
                </strong>
              </div>
              <span className="muted tiny">Top 5 (green) & Bottom 5 (red) performers • Max score: 100</span>
            </div>
          }
        >
          <div style={{ height: expandedWidgets.has('program-health') ? '500px' : '320px', transition: 'height 0.3s ease' }}>
            <Bar data={programHealthData} options={horizontalBarChartOptions} />
          </div>

          {/* Scoring Formula */}
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: 'var(--surface-page)',
            borderRadius: '6px',
            borderLeft: '3px solid var(--brand-primary)',
            fontSize: '11px',
            color: 'var(--text-body)'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', color: 'var(--text-strong)' }}>Health Score Formula (0-100):</div>
            <div style={{ display: 'grid', gap: '4px' }}>
              <div>• <strong>Completion Rate:</strong> 40 points</div>
              <div>• <strong>On-time Completion:</strong> 30 points</div>
              <div>• <strong>Engagement Rate:</strong> 20 points</div>
              <div>• <strong>Retention:</strong> 10 points</div>
            </div>
          </div>

          {/* Legend */}
          <div style={{ marginTop: '8px', fontSize: '10px', color: 'var(--text-muted)', borderTop: '1px solid var(--border-default)', paddingTop: '8px' }}>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <div><span style={{ display: 'inline-block', width: '12px', height: '12px', background: 'var(--status-success)', borderRadius: '2px', marginRight: '4px' }}></span>Top performer (70+)</div>
              <div><span style={{ display: 'inline-block', width: '12px', height: '12px', background: 'var(--brand-primary)', borderRadius: '2px', marginRight: '4px' }}></span>Good performer (50-69)</div>
              <div><span style={{ display: 'inline-block', width: '12px', height: '12px', background: 'var(--status-danger)', borderRadius: '2px', marginRight: '4px' }}></span>Needs attention (&lt;50)</div>
            </div>
          </div>
        </Widget>
        {/* Competency Heatmap - Filtered by analytics_dashboard=1 */}
        {DATA.competencies.length > 0 && DATA.competencyWeights.length > 0 && (
          <Widget
            id="competency-heatmap"
            style={{ overflow: 'auto' }}
            title={
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Competency Heatmap
                    <DataSourceBadge
                      source="api"
                      title="Competency Heatmap"
                      explanation="Competency weights per program from lesson plans API (/api/common/lesson-plans/active). Shows the percentage weight of each competency for each program."
                    />
                  </strong>
                </div>
                <span className="muted tiny">Course × Competency</span>
              </div>
            }
          >
            <table>
              <thead>
                <tr>
                  <th>Course/Program</th>
                  {DATA.competencies.map((comp, i) => (
                    <th key={i}>{comp}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DATA.courses.map((course, i) => (
                  <tr key={i}>
                    <td>{course}</td>
                    {DATA.competencyWeights.map((compRow, j) => (
                      <td
                        key={j}
                        className="heat"
                        style={{ background: colorScale(compRow[i]) }}
                      >
                        {compRow[i]}%
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Widget>
        )}
      </section>

      {/* Status Charts - Completion Rates */}
      <section className="grid g-2">
        <Widget
          id="cohort-status"
          title={
            <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              Program Cohort Status
              <InfoIcon tooltip="Shows the status of program cohorts based on their start and end dates. Status is determined by comparing today's date with the cohort's scheduled dates." />
              <DataSourceBadge
                source="api"
                title="Program Cohort Status"
                records={DATA.rawRecords?.activeProgramRecords}
                explanation="Status of program cohorts based on ModuleStartDate and ModuleEndDate. Completed = past end date, In Progress = between start and end dates, Not Started = before start date."
              />
            </strong>
          }
        >
          <div style={{ width: '220px', height: '220px', margin: '0 auto' }}>
            <Doughnut data={programStatusData} options={donutChartOptions} />
          </div>
          <div style={{ marginTop: '12px', fontSize: '11px', lineHeight: '1.8' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '12px', height: '12px', background: 'var(--status-success)', borderRadius: '2px', display: 'inline-block' }}></span>
              <span><strong>Completed:</strong> Past end date ({DATA.programDonut[0]} cohorts, {Math.round((DATA.programDonut[0] / DATA.programDonut.reduce((a,b)=>a+b,0)) * 100)}%)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '12px', height: '12px', background: 'var(--brand-primary)', borderRadius: '2px', display: 'inline-block' }}></span>
              <span><strong>In Progress:</strong> Currently running ({DATA.programDonut[1]} cohorts, {Math.round((DATA.programDonut[1] / DATA.programDonut.reduce((a,b)=>a+b,0)) * 100)}%)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ width: '12px', height: '12px', background: 'var(--status-warning)', borderRadius: '2px', display: 'inline-block' }}></span>
              <span><strong>Not Started:</strong> Before start date ({DATA.programDonut[2]} cohorts, {Math.round((DATA.programDonut[2] / DATA.programDonut.reduce((a,b)=>a+b,0)) * 100)}%)</span>
            </div>
          </div>
        </Widget>
        <Widget
          id="completion-rates"
          title={
            <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              Completion Rates by Cohort
              <InfoIcon tooltip="Shows the top performing program cohorts by completion rate. Only includes cohorts with at least 3 enrollments and some activity." />
              <DataSourceBadge
                source="api"
                title="Completion Rates by Cohort"
                records={DATA.rawRecords?.activeProgramRecords}
                explanation="Completion rate per cohort: (CompletedEnrollments / TotalEnrollments) × 100. Shows top 5 cohorts sorted by completion rate (best performers first)."
              />
            </strong>
          }
        >
          <div style={{ height: expandedWidgets.has('completion-rates') ? '400px' : '250px', transition: 'height 0.3s ease' }}>
            <Bar data={completionRatesData} options={horizontalBarChartOptions} />
          </div>
        </Widget>
      </section>

      {/* Enrollment Trends & Upcoming Deadlines */}
      <section className="grid g-3">
        <Widget
          id="training-health"
          title={
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Live Training Health
                  <DataSourceBadge source="calculated" />
                </strong>
              </div>
              <span className="muted tiny">
                Enrollment Funnel: Enrolled → Started → Completed
              </span>
            </div>
          }
        >
          <div style={{ height: expandedWidgets.has('training-health') ? '400px' : '200px', transition: 'height 0.3s ease' }}>
            <Bar data={trainingData} options={funnelChartOptions} />
          </div>
          <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
            {DATA.training.values[0] > 0 && (
              <>
                <div>Drop-off from Enrolled to Started: {DATA.training.values[0] - DATA.training.values[1]} ({Math.round(((DATA.training.values[0] - DATA.training.values[1]) / DATA.training.values[0]) * 100)}%)</div>
                <div>Drop-off from Started to Completed: {DATA.training.values[1] - DATA.training.values[2]} ({Math.round(((DATA.training.values[1] - DATA.training.values[2]) / DATA.training.values[1]) * 100)}%)</div>
                <div style={{ marginTop: '4px', fontWeight: 'bold', color: 'var(--status-success)' }}>Overall completion rate: {Math.round((DATA.training.values[2] / DATA.training.values[0]) * 100)}%</div>
              </>
            )}
          </div>
        </Widget>
        <Widget
          id="monthly-enrollments"
          title={
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Monthly Enrollments
                  <DataSourceBadge source="api" />
                </strong>
              </div>
              <span className="muted tiny">Trend</span>
            </div>
          }
        >
          <div style={{ height: expandedWidgets.has('monthly-enrollments') ? '400px' : '220px', transition: 'height 0.3s ease' }}>
            <Line data={enrollmentsData} options={lineChartOptions} />
          </div>
        </Widget>
        <Widget
          id="upcoming-deadlines"
          style={{ overflow: 'auto' }}
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Upcoming End Dates
                <DataSourceBadge source="api" />
              </strong>
              <span className="pill">
                Count: <span>{DATA.deadlinesRows.length}</span>
              </span>
            </div>
          }
        >
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Course <DataSourceBadge source="api" /></th>
                <th style={{ textAlign: 'left' }}>Cohort & Deadline <DataSourceBadge source="api" title="Cohort & Deadline" explanation="Cohort identifier from ModuleStartDate and ModuleEndDate" /></th>
                <th className="right">Learners <DataSourceBadge source="api" /></th>
              </tr>
            </thead>
            <tbody>
              {DATA.deadlinesRows.map((r, i) => (
                <tr key={i}>
                  <td style={{ textAlign: 'left' }}>{r.course}</td>
                  <td style={{ textAlign: 'left' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>👥</span>
                        <span>{r.cohort}</span>
                      </div>
                      <div style={{ fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span>📅</span>
                        <span>{r.end}</span>
                      </div>
                    </div>
                  </td>
                  <td className="right">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Widget>
      </section>
        </>
      )}

      {/* LEARNERS TAB */}
      {activeTab === 'learners' && (
        <>
      {/* At Risk Table - Full Width */}
      <section className="grid g-1">
        <Widget
          id="at-risk"
          style={{ overflow: 'auto' }}
          title={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '12px' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  At Risk – Catch Up Needed
                  <DataSourceBadge source="calculated" />
                </strong>
                <div className="row">
                  <span className="pill">
                    Count: <span>{totalRisks}</span>
                  </span>
                  {totalPages > 1 && (
                    <span className="muted tiny">
                      Page {currentPage} of {totalPages}
                    </span>
                  )}
                  <button onClick={exportCsv} className="btn tiny">
                    Export CSV
                  </button>
                  <button onClick={copyJson} className="btn tiny">
                    Copy JSON
                  </button>
                </div>
              </div>

              {/* Advanced Risk Intelligence Feature Box */}
              <div style={{
                  background: 'var(--gradient-brand)',
                  borderRadius: '12px',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease'
                }}>
                  {/* Header Bar - Always Visible */}
                  <div style={{
                    padding: '12px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    background: 'rgba(0, 0, 0, 0.1)'
                  }}
                  onClick={toggleRiskFeatureBox}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }}>
                      <span style={{ fontSize: '20px' }}>🚀</span>
                      <span>Advanced Risk Intelligence System</span>
                      <span style={{
                        fontSize: '11px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontWeight: 500
                      }}>
                        NEW
                      </span>
                    </div>
                    <span style={{ fontSize: '20px', transition: 'transform 0.3s', transform: riskFeatureBoxExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                      ▼
                    </span>
                  </div>

                  {/* Expandable Content */}
                  {riskFeatureBoxExpanded && (
                    <div style={{
                      padding: '16px',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '12px',
                      fontSize: '12px',
                      lineHeight: '1.6',
                      background: 'rgba(0, 0, 0, 0.05)'
                    }}>
                      <div>
                        <strong>✨ Composite Risk Scoring</strong>
                        <div style={{ opacity: 0.9, marginTop: '4px' }}>
                          Multi-factor algorithm combines overdue days, progress gaps, inactivity, and ROI potential into a single 0-100 risk score
                        </div>
                      </div>
                      <div>
                        <strong>🎯 Smart ROI Prioritization</strong>
                        <div style={{ opacity: 0.9, marginTop: '4px' }}>
                          Learners at 80%+ completion get boosted priority - highest ROI for intervention efforts
                        </div>
                      </div>
                      <div>
                        <strong>⏰ Proactive Risk Detection</strong>
                        <div style={{ opacity: 0.9, marginTop: '4px' }}>
                          Catches at-risk learners BEFORE they become overdue (7-day approaching deadline window)
                        </div>
                      </div>
                      <div>
                        <strong>🎨 4-Tier Risk Categories</strong>
                        <div style={{ opacity: 0.9, marginTop: '4px' }}>
                          Critical (21+ days overdue) → High (7-21 days) → Medium (1-7 days) → Approaching (preventive)
                        </div>
                      </div>
                      <div>
                        <strong>💡 Contextual Actions</strong>
                        <div style={{ opacity: 0.9, marginTop: '4px' }}>
                          Dynamic recommendations based on specific situation: payment issues, engagement level, deadline proximity
                        </div>
                      </div>
                      <div>
                        <strong>🔍 Advanced Filtering</strong>
                        <div style={{ opacity: 0.9, marginTop: '4px' }}>
                          Filter by risk category to focus on critical cases or preventive interventions
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              {/* Risk Category Filters */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => { setRiskFilter('all'); setCurrentPage(1); }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: riskFilter === 'all' ? '2px solid #3b82f6' : '1px solid #d1d5db',
                    background: riskFilter === 'all' ? '#eff6ff' : 'white',
                    color: riskFilter === 'all' ? '#3b82f6' : '#64748b',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  All ({riskCounts.all})
                </button>
                <button
                  onClick={() => { setRiskFilter('critical'); setCurrentPage(1); }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: riskFilter === 'critical' ? '2px solid #dc2626' : '1px solid #fecaca',
                    background: riskFilter === 'critical' ? '#fee2e2' : 'white',
                    color: 'var(--status-danger)',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  🔴 Critical ({riskCounts.critical})
                </button>
                <button
                  onClick={() => { setRiskFilter('high'); setCurrentPage(1); }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: riskFilter === 'high' ? '2px solid #ea580c' : '1px solid #fed7aa',
                    background: riskFilter === 'high' ? '#ffedd5' : 'white',
                    color: '#ea580c',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  🟠 High ({riskCounts.high})
                </button>
                <button
                  onClick={() => { setRiskFilter('medium'); setCurrentPage(1); }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: riskFilter === 'medium' ? '2px solid #d97706' : '1px solid #fde68a',
                    background: riskFilter === 'medium' ? '#fef3c7' : 'white',
                    color: 'var(--status-warning)',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  🟡 Medium ({riskCounts.medium})
                </button>
                <button
                  onClick={() => { setRiskFilter('approaching'); setCurrentPage(1); }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    border: riskFilter === 'approaching' ? '2px solid #2563eb' : '1px solid #bfdbfe',
                    background: riskFilter === 'approaching' ? '#dbeafe' : 'white',
                    color: 'var(--brand-primary)',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  ⏰ Approaching ({riskCounts.approaching})
                </button>
              </div>
            </div>
          }
        >
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', width: '40px' }}>Risk</th>
                <th style={{ textAlign: 'left' }}>Learner <DataSourceBadge source="api" apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`} /></th>
                <th style={{ textAlign: 'left' }}>Course <DataSourceBadge source="api" apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`} /></th>
                <th className="right" style={{ width: '60px' }}>Progress <DataSourceBadge source="api" title="Progress" explanation="Actual progress percentage from enrollment API. Falls back to status-based estimation if not available." apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`} /></th>
                <th className="right" style={{ width: '65px' }}>Expected <DataSourceBadge source="calculated" title="Expected Progress" explanation="Calculated as: (today - ModuleStartDate) / (ModuleEndDate - ModuleStartDate) × 100. Shows how far along learners should be based on time elapsed." /></th>
                <th className="right" style={{ width: '80px' }}>Deadline <DataSourceBadge source="calculated" title="Deadline Status" explanation="Shows days overdue (negative) or days until deadline (positive)." /></th>
                <th style={{ width: '130px' }}>Last Seen <DataSourceBadge source="api" apiUrl={`${Config.baseUrl}api/common/lms-admin-dashboard`} /></th>
                <th style={{ width: '180px' }}>Action Needed <DataSourceBadge source="calculated" title="Action Needed" explanation="Contextual recommendation based on risk category, progress, and activity." /></th>
                <th style={{ width: '70px', textAlign: 'center' }}>Nudge</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRisks.map((r, i) => {
                // Determine row background color based on risk category
                const getRowStyle = () => {
                  const baseStyle = { verticalAlign: 'middle' as const };
                  switch (r.riskCategory) {
                    case 'critical':
                      return { ...baseStyle, backgroundColor: 'var(--status-danger-bg)' };
                    case 'high':
                      return { ...baseStyle, backgroundColor: '#fff7ed' };
                    case 'medium':
                      return { ...baseStyle, backgroundColor: 'var(--status-warning-bg)' };
                    case 'approaching':
                      return { ...baseStyle, backgroundColor: 'var(--status-info-bg)' };
                    default:
                      return baseStyle;
                  }
                };

                // Get risk icon
                const getRiskIcon = () => {
                  switch (r.riskCategory) {
                    case 'critical': return '🔴';
                    case 'high': return '🟠';
                    case 'medium': return '🟡';
                    case 'approaching': return '⏰';
                    default: return '⚪';
                  }
                };

                return (
                  <tr key={i} style={getRowStyle()}>
                    <td style={{ verticalAlign: 'middle', textAlign: 'center', fontSize: '16px' }} title={`Risk: ${r.riskCategory} (Score: ${r.riskScore})`}>
                      {getRiskIcon()}
                    </td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>{r.learner}</td>
                    <td style={{ verticalAlign: 'middle', textAlign: 'left' }}>{r.course}</td>
                    <td className="right" style={{ verticalAlign: 'middle' }}>{r.progress}%</td>
                    <td className="right" style={{ verticalAlign: 'middle' }}>{r.expected}%</td>
                    <td className="right" style={{ verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        {r.daysOverdue !== undefined && r.daysOverdue > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '14px' }}>⏰</span>
                            <span style={{
                              fontWeight: 600,
                              color: r.daysOverdue > 14 ? '#dc2626' : '#ea580c'
                            }}>
                              {r.daysOverdue}d overdue
                            </span>
                            {r.daysOverdue > 14 && <span style={{ fontSize: '14px' }}>🔴</span>}
                          </div>
                        ) : r.daysUntilDeadline !== undefined ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '14px' }}>📅</span>
                            <span style={{
                              fontWeight: 600,
                              color: r.daysUntilDeadline <= 3 ? '#ea580c' : '#2563eb'
                            }}>
                              {r.daysUntilDeadline}d left
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle', minWidth: '100px', maxWidth: '120px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-body)' }}>
                          <span style={{ fontSize: '14px' }}>📅</span>
                          <span style={{ fontWeight: 500 }}>{r.last.split(' ')[0]}</span>
                        </div>
                        {r.flags.length > 0 && (
                          <div style={{ display: 'flex', gap: '6px', paddingLeft: '20px' }}>
                            {r.flags.map((f, fi) => (
                              <span
                                key={fi}
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  color: 'var(--status-danger)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '3px'
                                }}
                              >
                                ⚠️ {f}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ verticalAlign: 'middle' }}>
                      <span
                        style={{
                          background: r.actionNeeded.includes('Critical') || r.actionNeeded.includes('Urgent') ? '#fee2e2' :
                                     r.actionNeeded.includes('payment') ? '#fef3c7' :
                                     r.actionNeeded.includes('Overdue') ? '#ffedd5' :
                                     r.actionNeeded.includes('Warning') ? '#fef9c3' :
                                     r.actionNeeded.includes('Almost done') ? '#dcfce7' : '#f0fdf4',
                          color: r.actionNeeded.includes('Critical') || r.actionNeeded.includes('Urgent') ? '#7f1d1d' :
                                r.actionNeeded.includes('payment') ? '#92400e' :
                                r.actionNeeded.includes('Overdue') ? '#9a3412' :
                                r.actionNeeded.includes('Warning') ? '#854d0e' :
                                r.actionNeeded.includes('Almost done') ? '#166534' : '#166534',
                          fontWeight: r.actionNeeded.includes('Critical') || r.actionNeeded.includes('Urgent') ? 600 : 500,
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          display: 'inline-block'
                        }}
                      >
                        {r.actionNeeded}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => openManualNudgePanel(r.learner, r.course, undefined, undefined)}
                        className="btn tiny"
                        style={{
                          padding: '4px 8px',
                          fontSize: '11px',
                          background: 'var(--accent)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                        title="Send manual nudge to this learner"
                      >
                        📧
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid var(--border)'
            }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn"
                style={{
                  opacity: currentPage === 1 ? 0.4 : 1,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous
              </button>
              <span className="muted tiny">
                Showing {startIndex + 1}-{Math.min(endIndex, totalRisks)} of {totalRisks}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="btn"
                style={{
                  opacity: currentPage === totalPages ? 0.4 : 1,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
            </div>
          )}
        </Widget>
      </section>

      {/* Learning Overview - Combined Status + Effectiveness */}
      <section className="grid g-1">
        <Widget
          id="learning-overview"
          title={
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Learning Overview
                  <DataSourceBadge source="api" />
                </strong>
              </div>
              <span className="muted tiny">
                Enrollment status • Learning effectiveness • CSAT • Course breakout below
              </span>
            </div>
          }
        >
          {/* Two-column layout: Status (left) + Effectiveness (right) */}
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px', alignItems: 'start' }}>
            {/* LEFT: Learner Records Status */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <strong style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Learner Records Status
                  <InfoIcon tooltip="Shows the distribution of all enrollment records by their current status. Each record represents one learner's enrollment in a program." />
                  <DataSourceBadge
                    source="api"
                    title="Learner Records Status"
                    records={[
                      { Status: 'COMPLETED', Count: DATA.rawRecords?.completedRecords?.length || 0 },
                      { Status: 'STARTED', Count: DATA.rawRecords?.startedRecords?.length || 0 },
                      { Status: 'ASSIGNED', Count: DATA.rawRecords?.assignedRecords?.length || 0 }
                    ]}
                    explanation="Direct count from Status field in API: COMPLETED, STARTED, ASSIGNED"
                  />
                </strong>
              </div>
              <div style={{ width: '180px', height: '180px', margin: '0 auto' }}>
                <Doughnut data={learningStatusData} options={donutChartOptions} />
              </div>
              <div style={{ marginTop: '12px', fontSize: '11px', lineHeight: '1.6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '10px', height: '10px', background: 'var(--status-success)', borderRadius: '2px', display: 'inline-block' }}></span>
                  <span><strong>Completed:</strong> {DATA.learningDonut[0]} ({Math.round((DATA.learningDonut[0] / DATA.learningDonut.reduce((a,b)=>a+b,0)) * 100)}%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '10px', height: '10px', background: 'var(--brand-primary)', borderRadius: '2px', display: 'inline-block' }}></span>
                  <span><strong>In Progress:</strong> {DATA.learningDonut[1]} ({Math.round((DATA.learningDonut[1] / DATA.learningDonut.reduce((a,b)=>a+b,0)) * 100)}%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '10px', height: '10px', background: 'var(--status-warning)', borderRadius: '2px', display: 'inline-block' }}></span>
                  <span><strong>Not Started:</strong> {DATA.learningDonut[2]} ({Math.round((DATA.learningDonut[2] / DATA.learningDonut.reduce((a,b)=>a+b,0)) * 100)}%)</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '10px', height: '10px', background: 'var(--status-danger)', borderRadius: '2px', display: 'inline-block' }}></span>
                  <span><strong>Paused:</strong> {DATA.learningDonut[3]} ({Math.round((DATA.learningDonut[3] / DATA.learningDonut.reduce((a,b)=>a+b,0)) * 100)}%)</span>
                </div>
              </div>
            </div>

            {/* RIGHT: Learning Effectiveness */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <strong style={{ fontSize: '13px' }}>Learning Effectiveness (Baseline vs Endline)</strong>
              </div>
              <div style={{ height: '200px' }}>
                <Bar data={prePostData} options={horizontalBarChartOptions} />
              </div>
            </div>
          </div>

          {/* CSAT and Course Breakout */}
          <div style={{ borderTop: '1px solid var(--border-default)', marginTop: '16px', paddingTop: '16px' }}>
            <div className="tiny" style={{ marginBottom: '4px' }}>
              CSAT
            </div>
            <div
              style={{
                background: 'var(--surface-sunken)',
                borderRadius: '999px',
                height: '8px',
                width: '100%',
              }}
            >
              <div
                style={{
                  height: '8px',
                  borderRadius: '999px',
                  background: 'var(--status-success)',
                  width: `${DATA.csat}%`,
                }}
              ></div>
            </div>
            <div className="tiny">{DATA.csat}%</div>
            <div className="tiny" style={{ marginTop: '10px' }}>
              Breakout by Course
            </div>
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Course</th>
                  <th className="right">Baseline</th>
                  <th className="right">Endline</th>
                  <th className="right">Gain</th>
                  <th className="right">CSAT</th>
                </tr>
              </thead>
              <tbody>
                {effectivenessRows.map((r, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: 'left' }}>{r.course}</td>
                    <td className="right">{r.pre}%</td>
                    <td className="right">{r.post}%</td>
                    <td className="right">+{r.gain}</td>
                    <td className="right">{r.csat}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Widget>
      </section>
        </>
      )}

      {/* INSIGHTS TAB */}
      {activeTab === 'insights' && (
        <>
      <section className="grid g-1">
        <Widget
          id="ai-insights"
          title={
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  AI‑Based Insights
                  <DataSourceBadge
                    source={aiInsights.data ? "api" : "calculated"}
                    title="AI-Based Insights"
                    explanation={aiInsights.data
                      ? "AI-generated insights from learning analytics API using generateLearningAnalytics"
                      : "Click to generate predictions based on your LMS data"
                    }
                  />
                </strong>
              </div>
              <span className="muted tiny">
                {aiInsights.data ? 'AI Generated' : 'Generate Insights'}
              </span>
            </div>
          }
        >
          {aiInsights.loading ? (
            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>🤖</div>
              <div style={{ fontSize: '12px' }}>Generating AI insights...</div>
            </div>
          ) : aiInsights.error ? (
            <div style={{ textAlign: 'center', padding: '32px 20px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--status-danger)', marginBottom: '12px' }}>
                {aiInsights.error}
              </div>
              {(aiInsights.error.includes('Authentication') || aiInsights.error.includes('login')) ? (
                <a
                  href="/login"
                  style={{
                    display: 'inline-block',
                    background: 'var(--brand-primary)',
                    color: 'white',
                    padding: '10px 24px',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    marginTop: '8px',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--brand-primary)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--brand-primary)'}
                >
                  🔑 Login Now
                </a>
              ) : (
                <button
                  onClick={fetchAiInsights}
                  style={{
                    background: 'var(--brand-primary)',
                    color: 'white',
                    padding: '10px 24px',
                    borderRadius: '6px',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    marginTop: '8px'
                  }}
                >
                  🔄 Try Again
                </button>
              )}
            </div>
          ) : aiInsights.data ? (
            <>
              {/* Key Metrics Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginBottom: '20px'
              }}>
                <div style={{
                  background: 'var(--gradient-brand)',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  color: 'white',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '6px' }}>Predicted Overdue (30d)</div>
                  <div style={{ fontSize: '26px', fontWeight: 'bold' }}>{predicted}</div>
                  <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '2px' }}>learners at risk</div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  color: 'white',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '6px' }}>Top At-Risk Competency</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', lineHeight: '1.2' }}>{topRiskComp}</div>
                </div>

                <div style={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  borderRadius: '8px',
                  padding: '14px 16px',
                  color: 'white',
                  minHeight: '90px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <div style={{ fontSize: '11px', opacity: 0.9, marginBottom: '6px' }}>Recommended Module</div>
                  <div style={{ fontSize: '15px', fontWeight: 'bold', lineHeight: '1.3' }}>{recModule}</div>
                </div>
              </div>

              {/* Actionable Insights */}
              {aiInsights.data?.additionalInsights && aiInsights.data.additionalInsights.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: 'var(--text-strong)',
                    marginBottom: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ fontSize: '16px' }}>💡</span>
                    <span>Key Insights & Recommendations</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {aiInsights.data.additionalInsights.map((insight, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: 'var(--surface-page)',
                          border: '1px solid var(--border-default)',
                          borderLeft: '4px solid var(--brand-primary)',
                          borderRadius: '8px',
                          padding: '14px 16px',
                          fontSize: '13px',
                          lineHeight: '1.6',
                          color: 'var(--text-strong)',
                          minHeight: '60px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <span style={{ fontWeight: 700, color: 'var(--brand-primary)', marginRight: '8px', fontSize: '14px' }}>#{idx + 1}</span>
                        <span>{insight}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={fetchAiInsights}
                className="btn tiny"
                style={{
                  background: 'var(--gradient-brand)',
                  color: 'white',
                  width: '100%',
                  border: 'none',
                  padding: '10px',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 4px rgba(102, 126, 234, 0.3)';
                }}
              >
                🔄 Regenerate Insights
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '32px 20px' }}>
              <div style={{
                fontSize: '48px',
                marginBottom: '16px',
                background: 'var(--gradient-brand)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                🤖
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-strong)',
                marginBottom: '8px'
              }}>
                AI-Powered Analytics Ready
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-muted)',
                marginBottom: '20px',
                lineHeight: '1.5'
              }}>
                Generate intelligent insights based on your complete LMS data including enrollments, lesson plans, and competencies
              </div>
              <button
                onClick={fetchAiInsights}
                className="btn tiny"
                style={{
                  background: 'var(--gradient-brand)',
                  color: 'white',
                  width: '100%',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                  fontSize: '13px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                }}
              >
                ✨ Generate AI Insights
              </button>
            </div>
          )}
        </Widget>
      </section>

      {/* AI Chat Assistant */}
      <section className="grid g-1">
        <Widget
          id="ai-chat"
          title={
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  💬 AI Chat Assistant
                  <DataSourceBadge
                    source="api"
                    title="AI Chat Assistant"
                    explanation="Interactive chat powered by AI with full access to your LMS data (enrollments, lesson plans, competencies)"
                  />
                </strong>
              </div>
              <span className="muted tiny">
                Ask questions about your LMS data
              </span>
            </div>
          }
        >
          <AiChat data={DATA} showDataSource={showDataSource} expandedWidgets={expandedWidgets} />
        </Widget>
      </section>
        </>
      )}

      {/* SKILLS TAB */}
      {activeTab === 'skills' && DATA.learnerSkillMatrix && DATA.learnerSkillMatrix.length > 0 && (
        <>
      {/* Gap Analysis - Two widgets side by side */}
      <section className="grid g-2">
          <Widget
            id="competency-gap-summary"
            title={
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    📊 Competency Gap Analysis
                    <InfoIcon tooltip={`Shows which competencies have the most learners struggling (below 60% proficiency).

Status Logic:
• Critical: ≥50% of learners below threshold
• Needs Attention: 30-49% below threshold
• Monitor: 1-29% below threshold
• On Track: 0% below threshold

This helps prioritize which competencies need immediate intervention.`} />
                    <DataSourceBadge
                      source="api"
                      title="Competency Gap Analysis"
                      explanation="Calculated from learner-topic-analysis API. Shows percentage of learners below 60% proficiency threshold for each competency."
                    />
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <span className="muted tiny">Identify competencies needing attention</span>
                  <a
                    onClick={() => exportToCSV(
                      DATA.competencyGapSummary || [],
                      'competency-gap-analysis',
                      ['competency', 'totalLearners', 'learnersWithGap', 'gapPercentage', 'avgProficiency', 'status']
                    )}
                    style={{
                      color: 'var(--brand-primary)',
                      fontSize: '11px',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      flexShrink: 0
                    }}
                  >
                    CSV
                  </a>
                </div>
              </div>
            }
          >
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Competency</th>
                  <th className="right">Learners</th>
                  <th className="right">Gap</th>
                  <th className="right">Avg</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {DATA.competencyGapSummary?.slice(0, competencyGapPage * skillsPageSize).map((comp, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: 'left', fontWeight: 500 }}>{comp.competency}</td>
                    <td className="right">
                      <span style={{ color: 'var(--text-muted)' }}>{comp.learnersWithGap}</span>
                      <span style={{ color: 'var(--text-faint)', fontSize: '11px' }}>/{comp.totalLearners}</span>
                    </td>
                    <td className="right">
                      <span style={{
                        fontWeight: 600,
                        color: comp.gapPercentage > 50 ? '#dc2626' : comp.gapPercentage > 25 ? '#ea580c' : '#10b981'
                      }}>
                        {comp.gapPercentage}%
                      </span>
                    </td>
                    <td className="right">
                      <span style={{
                        fontWeight: 500,
                        color: comp.avgProficiency >= 60 ? '#10b981' : comp.avgProficiency >= 40 ? '#ea580c' : '#dc2626'
                      }}>
                        {comp.avgProficiency}%
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          background: comp.status === 'Critical' ? '#fee2e2'
                            : comp.status === 'Needs Attention' ? '#ffedd5'
                            : comp.status === 'Monitor' ? '#dbeafe'
                            : '#dcfce7',
                          color: comp.status === 'Critical' ? '#7f1d1d'
                            : comp.status === 'Needs Attention' ? '#7c2d12'
                            : comp.status === 'Monitor' ? '#1e3a8a'
                            : '#166534',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 600,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {comp.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {DATA.competencyGapSummary && DATA.competencyGapSummary.length > competencyGapPage * skillsPageSize && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <button
                  onClick={() => setCompetencyGapPage(competencyGapPage + 1)}
                  className="btn"
                  style={{ padding: '6px 16px' }}
                >
                  Load More ({DATA.competencyGapSummary.length - competencyGapPage * skillsPageSize} remaining)
                </button>
              </div>
            )}
          </Widget>

          <Widget
            id="skill-gaps"
            style={{ overflow: 'auto' }}
            title={
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    🎯 Learner Skill Gaps
                    <InfoIcon tooltip="Individual learners with competencies below 60% proficiency threshold. Sorted by gap size (largest first)." />
                    <DataSourceBadge
                      source="api"
                      title="Learner Skill Gaps"
                      explanation="From learner-topic-analysis API. Shows learners who scored below 60% on specific competency assessments."
                    />
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <span className="muted tiny">Target interventions for specific learners</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                    <span className="pill">
                      Count: <span>{DATA.skillGaps?.length || 0}</span>
                    </span>
                    <a
                      onClick={() => exportToCSV(
                        DATA.skillGaps || [],
                        'learner-skill-gaps',
                        ['learnerName', 'userId', 'competency', 'currentProficiency', 'targetProficiency', 'gap', 'severity']
                      )}
                      style={{
                        color: 'var(--brand-primary)',
                        fontSize: '11px',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      CSV
                    </a>
                  </div>
                </div>
              </div>
            }
          >
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Learner</th>
                  <th style={{ textAlign: 'left' }}>Competency</th>
                  <th className="right">Current</th>
                  <th className="right">Target</th>
                  <th className="right">Gap</th>
                  <th>Severity</th>
                </tr>
              </thead>
              <tbody>
                {DATA.skillGaps?.slice(0, learnerGapPage * skillsPageSize).map((gap, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: 'left' }}>{gap.learnerName}</td>
                    <td style={{ textAlign: 'left' }}>{gap.competency}</td>
                    <td className="right">
                      <span style={{
                        fontWeight: 500,
                        color: gap.currentProficiency >= 50 ? '#ea580c' : '#dc2626'
                      }}>
                        {gap.currentProficiency}%
                      </span>
                    </td>
                    <td className="right">{gap.targetProficiency}%</td>
                    <td className="right">
                      <span style={{
                        fontWeight: 600,
                        color: gap.gap > 30 ? '#dc2626' : gap.gap > 15 ? '#ea580c' : '#d97706'
                      }}>
                        {gap.gap}%
                      </span>
                    </td>
                    <td>
                      <span
                        style={{
                          background: gap.severity === 'Critical' ? '#fee2e2' :
                                     gap.severity === 'High' ? '#ffedd5' : '#fef3c7',
                          color: gap.severity === 'Critical' ? '#7f1d1d' :
                                gap.severity === 'High' ? '#9a3412' : '#92400e',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 600
                        }}
                      >
                        {gap.severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {DATA.skillGaps && DATA.skillGaps.length > learnerGapPage * skillsPageSize && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <button
                  onClick={() => setLearnerGapPage(learnerGapPage + 1)}
                  className="btn"
                  style={{ padding: '6px 16px' }}
                >
                  Load More ({DATA.skillGaps.length - learnerGapPage * skillsPageSize} remaining)
                </button>
              </div>
            )}
          </Widget>
        </section>

      {/* Learning Gains - Full width */}
      <section className="grid g-1">
          <Widget
            id="learning-gains"
            style={{ overflow: 'auto' }}
            title={
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    📈 Learning Gains (Baseline → Endline)
                    <InfoIcon tooltip="Shows improvement from pre-test (baseline) to post-test (endline). Positive gains indicate learning progress." />
                    <DataSourceBadge
                      source="api"
                      title="Learning Gains"
                      explanation="Calculated from baseline (testcategory=1001) and endline (testcategory=1002) topic analysis APIs. Shows the difference in competency scores."
                    />
                  </strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <span className="muted tiny">Measure training effectiveness by competency</span>
                  <span className="pill" style={{ flexShrink: 0 }}>
                    Count: <span>{DATA.learningGains?.length || 0}</span>
                  </span>
                </div>
              </div>
            }
          >
            {DATA.learningGains && DATA.learningGains.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Learner</th>
                    <th style={{ textAlign: 'left' }}>Competency</th>
                    <th className="right">Baseline</th>
                    <th className="right">Endline</th>
                    <th className="right">Gain</th>
                    <th>Category</th>
                  </tr>
                </thead>
                <tbody>
                  {DATA.learningGains.slice(0, learningGainsPage * skillsPageSize).map((gain, i) => (
                    <tr key={i}>
                      <td style={{ textAlign: 'left' }}>{gain.learnerName}</td>
                      <td style={{ textAlign: 'left' }}>{gain.competency}</td>
                      <td className="right">
                        <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>
                          {gain.baselinePercentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="right">
                        <span style={{
                          fontWeight: 500,
                          color: gain.endlinePercentage >= 60 ? '#10b981' : gain.endlinePercentage >= 40 ? '#ea580c' : '#dc2626'
                        }}>
                          {gain.endlinePercentage.toFixed(1)}%
                        </span>
                      </td>
                      <td className="right">
                        <span style={{
                          fontWeight: 600,
                          color: gain.learningGain >= 10 ? '#10b981' : gain.learningGain >= 0 ? '#3b82f6' : '#dc2626'
                        }}>
                          {gain.learningGain > 0 ? '+' : ''}{gain.learningGain.toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            background: gain.gainCategory === 'Excellent' ? '#dcfce7' :
                                       gain.gainCategory === 'Good' ? '#dbeafe' :
                                       gain.gainCategory === 'Moderate' ? '#fef3c7' :
                                       gain.gainCategory === 'Minimal' ? '#f3f4f6' : '#fee2e2',
                            color: gain.gainCategory === 'Excellent' ? '#166534' :
                                  gain.gainCategory === 'Good' ? '#1e40af' :
                                  gain.gainCategory === 'Moderate' ? '#92400e' :
                                  gain.gainCategory === 'Minimal' ? '#4b5563' : '#7f1d1d',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: 600
                          }}
                        >
                          {gain.gainCategory === 'Excellent' && '🌟 '}
                          {gain.gainCategory === 'Good' && '✨ '}
                          {gain.gainCategory === 'Declined' && '📉 '}
                          {gain.gainCategory}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                <div style={{ fontSize: '14px' }}>No learning gains data available</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>
                  Ensure learners have completed both baseline and endline assessments
                </div>
              </div>
            )}
            {DATA.learningGains && DATA.learningGains.length > learningGainsPage * skillsPageSize && (
              <div style={{ textAlign: 'center', marginTop: '16px' }}>
                <button
                  onClick={() => setLearningGainsPage(learningGainsPage + 1)}
                  className="btn"
                  style={{ padding: '6px 16px' }}
                >
                  Load More ({DATA.learningGains.length - learningGainsPage * skillsPageSize} remaining)
                </button>
              </div>
            )}
          </Widget>
        </section>
        </>
      )}

      {/* SKILLS TAB - empty state */}
      {activeTab === 'skills' && (!DATA.learnerSkillMatrix || DATA.learnerSkillMatrix.length === 0) && (
        <section className="grid">
          <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎓</div>
            <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px' }}>
              No skill &amp; gap data available
            </div>
            <div className="muted" style={{ maxWidth: '560px', margin: '0 auto', lineHeight: 1.5 }}>
              This view compares baseline (pre-test) and endline (post-test) competency
              assessments. No assessment data was found for test categories
              <strong> 1001</strong> (baseline) and <strong>1002</strong> (endline).
              <br />
              Once learners complete tests under these categories, competency gap analysis,
              skill gaps, and learning gains will appear here.
            </div>
          </div>
        </section>
      )}

      {/* NUDGE DASHBOARD TAB */}
      {activeTab === 'nudge' && (
        <>
          {/* Tagline */}
          <div style={{ marginBottom: '20px', fontStyle: 'italic', color: 'var(--muted)', fontSize: '14px' }}>
            Track how often learners were reminded, encouraged, or prompted.
          </div>

          {/* Overview Stats */}
          <section className="grid g-2">
            <div className="card">
              <div className="muted tiny">Total Nudges Sent</div>
              <div className="kpi">{nudgeOverview?.summary.totalNudgesSent || 0}</div>
            </div>
            <div className="card">
              <div className="muted tiny">Unique Learners</div>
              <div className="kpi">{nudgeOverview?.summary.uniqueLearners || 0}</div>
            </div>
            <div className="card">
              <div className="muted tiny">Automated Nudges</div>
              <div className="kpi">{nudgeOverview?.summary.automatedNudges || 0}</div>
            </div>
            <div className="card">
              <div className="muted tiny">Manual Nudges</div>
              <div className="kpi">{nudgeOverview?.summary.manualNudges || 0}</div>
            </div>
          </section>

          {/* Channel Breakdown & Categories */}
          {nudgeOverview && (
            <section className="grid g-2">
              <Widget id="nudge-channels" title={<strong>Nudges by Channel</strong>}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Channel</th>
                      <th className="right">Sent</th>
                      <th className="right">Delivered</th>
                      <th className="right">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nudgeOverview.byChannel.map((ch: any, i: number) => (
                      <tr key={i}>
                        <td style={{ textAlign: 'left', textTransform: 'capitalize' }}>{ch.channel}</td>
                        <td className="right">{ch.count}</td>
                        <td className="right">{ch.delivered}</td>
                        <td className="right">
                          <span style={{ fontWeight: 600, color: ch.delivered / ch.count > 0.9 ? '#10b981' : '#ea580c' }}>
                            {((ch.delivered / ch.count) * 100).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Widget>

              <Widget id="nudge-categories" title={<strong>Nudges by Category</strong>}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Category</th>
                      <th className="right">Count</th>
                      <th className="right">Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nudgeOverview.byCategory.map((cat: any, i: number) => {
                      const total = nudgeOverview.summary.totalNudgesSent;
                      return (
                        <tr key={i}>
                          <td style={{ textAlign: 'left', textTransform: 'capitalize' }}>{cat.category}</td>
                          <td className="right">{cat.count}</td>
                          <td className="right">{((cat.count / total) * 100).toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Widget>
            </section>
          )}

          {/* Nudge Rules */}
          <section className="grid g-1">
            <Widget
              id="nudge-rules"
              title={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <strong>Nudge Rules</strong>
                  <button
                    onClick={() => setNewRuleModal(true)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      background: 'var(--brand-primary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    + New Rule
                  </button>
                </div>
              }
            >
              <table>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Rule Name</th>
                    <th style={{ textAlign: 'left' }}>Description</th>
                    <th className="right">Times Triggered</th>
                    <th className="right">Unique Learners</th>
                    <th style={{ textAlign: 'center', width: '80px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Build trigger statistics from history
                    const triggerStats = new Map();
                    nudgeHistory.forEach((nudge: any) => {
                      if (nudge.ruleId !== null) {
                        if (!triggerStats.has(nudge.ruleId)) {
                          triggerStats.set(nudge.ruleId, {
                            count: 0,
                            learners: new Set()
                          });
                        }
                        const stats = triggerStats.get(nudge.ruleId);
                        stats.count++;
                        stats.learners.add(nudge.learnerId);
                      }
                    });

                    // Use rules from API and merge with trigger stats
                    return nudgeRules.map((rule, i) => {
                      const stats = triggerStats.get(rule.id) || { count: 0, learners: new Set() };
                      const isActive = rule.isActive === 1 || rule.isActive === true;

                      return (
                        <tr key={i} style={{ opacity: isActive ? 1 : 0.5 }}>
                          <td style={{ textAlign: 'left', fontWeight: 500 }}>
                            {rule.name}
                            {!isActive && <span style={{ marginLeft: '8px', fontSize: '11px', color: 'var(--muted)' }}>(Inactive)</span>}
                          </td>
                          <td style={{ textAlign: 'left', fontSize: '12px', color: 'var(--muted)' }}>{rule.description}</td>
                          <td className="right">{stats.count}</td>
                          <td className="right">{stats.learners.size}</td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              onClick={() => deleteNudgeRule(rule.id, rule.name)}
                              className="btn tiny"
                              style={{
                                padding: '4px 8px',
                                fontSize: '11px',
                                background: 'var(--status-danger)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                              title="Delete this rule"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
              {nudgeHistory.filter((n: any) => n.ruleId === null).length > 0 && (
                <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--muted)' }}>
                  Note: {nudgeHistory.filter((n: any) => n.ruleId === null).length} manual nudges sent (not listed above)
                </div>
              )}
            </Widget>
          </section>

          {/* Nudge History */}
          <section className="grid g-1">
            <Widget
              id="nudge-history"
              style={{ overflow: 'auto' }}
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <strong>Nudge History</strong>
                  <span className="pill">Total: {nudgeHistoryTotal}</span>
                </div>
              }
            >
              {/* Filters - Always visible */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', padding: '12px', background: 'var(--surface-page)', borderRadius: '8px', flexWrap: 'wrap' }}>
                <select
                  value={nudgeFilters.channel}
                  onChange={(e) => {
                    setNudgeFilters({ ...nudgeFilters, channel: e.target.value });
                    setNudgeHistoryPage(1);
                  }}
                  style={{ flex: '1 1 150px', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-default)' }}
                >
                  <option value="">All Channels</option>
                  <option value="email">Email</option>
                  <option value="sms">SMS</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="in_app">In-App</option>
                </select>
                <input
                  type="date"
                  value={nudgeFilters.startDate}
                  onChange={(e) => {
                    setNudgeFilters({ ...nudgeFilters, startDate: e.target.value });
                    setNudgeHistoryPage(1);
                  }}
                  placeholder="Start Date"
                  style={{ flex: '1 1 150px', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-default)' }}
                />
                <input
                  type="date"
                  value={nudgeFilters.endDate}
                  onChange={(e) => {
                    setNudgeFilters({ ...nudgeFilters, endDate: e.target.value });
                    setNudgeHistoryPage(1);
                  }}
                  placeholder="End Date"
                  style={{ flex: '1 1 150px', padding: '6px 8px', borderRadius: '6px', border: '1px solid var(--border-default)' }}
                />
                <button
                  onClick={() => fetchNudgeHistory(1)}
                  className="btn"
                  style={{ padding: '6px 16px' }}
                >
                  Apply Filters
                </button>
                <button
                  onClick={() => {
                    setNudgeFilters({ channel: '', startDate: '', endDate: '', learnerId: '' });
                    setNudgeHistoryPage(1);
                    fetchNudgeHistory(1);
                  }}
                  className="btn"
                  style={{ padding: '6px 16px', background: 'var(--surface-sunken)', color: 'var(--text-body)' }}
                >
                  Clear Filters
                </button>
              </div>

              {nudgeHistory.length > 0 ? (
                <>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Learner</th>
                        <th style={{ textAlign: 'left' }}>Course</th>
                        <th>Channel</th>
                        <th style={{ textAlign: 'left' }}>Rule</th>
                        <th>Sent At</th>
                        <th>Status</th>
                        <th>Engagement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nudgeHistory.map((nudge: any) => (
                        <tr key={nudge.id}>
                          <td style={{ textAlign: 'left' }}>
                            <span
                              onClick={() => fetchLearnerDetail(nudge.learnerId)}
                              style={{
                                color: 'var(--brand-primary)',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationStyle: 'dotted'
                              }}
                              title="View learner preferences and stats"
                            >
                              {nudge.learnerName}
                            </span>
                          </td>
                          <td style={{ textAlign: 'left' }}>{nudge.courseName}</td>
                          <td style={{ textTransform: 'capitalize', textAlign: 'center' }}>{nudge.channel}</td>
                          <td style={{ textAlign: 'left', fontSize: '12px' }}>{nudge.ruleName}</td>
                          <td style={{ textAlign: 'center', fontSize: '12px' }}>
                            {new Date(nudge.sentAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td style={{ textAlign: 'left' }}>
                            <span style={{
                              background: nudge.deliveryStatus === 'delivered' ? '#dcfce7' :
                                         nudge.deliveryStatus === 'failed' ? '#fee2e2' : '#fef3c7',
                              color: nudge.deliveryStatus === 'delivered' ? '#166534' :
                                    nudge.deliveryStatus === 'failed' ? '#7f1d1d' : '#92400e',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap'
                            }}>
                              {nudge.deliveryStatus}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                              {nudge.opened && (
                                <span
                                  title="Opened"
                                  style={{
                                    display: 'inline-block',
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    background: 'var(--brand-primary)'
                                  }}
                                />
                              )}
                              {nudge.clicked && (
                                <span
                                  title="Clicked"
                                  style={{
                                    display: 'inline-block',
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    background: 'var(--brand-accent)'
                                  }}
                                />
                              )}
                              {nudge.actionTaken && (
                                <span
                                  title="Action Taken"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '18px',
                                    height: '18px',
                                    borderRadius: '50%',
                                    background: 'var(--status-success)',
                                    color: 'white',
                                    fontSize: '10px',
                                    fontWeight: 600
                                  }}
                                >
                                  ✓
                                </span>
                              )}
                              {!nudge.opened && !nudge.clicked && !nudge.actionTaken && <span style={{ color: 'var(--text-faint)' }}>-</span>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {nudgeHistoryTotal > 25 && (
                    <div style={{ textAlign: 'center', marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <button
                        onClick={() => fetchNudgeHistory(nudgeHistoryPage - 1)}
                        disabled={nudgeHistoryPage === 1}
                        className="btn"
                        style={{ opacity: nudgeHistoryPage === 1 ? 0.4 : 1 }}
                      >
                        ← Previous
                      </button>
                      <span className="muted tiny">
                        Page {nudgeHistoryPage} of {Math.ceil(nudgeHistoryTotal / 25)}
                      </span>
                      <button
                        onClick={() => fetchNudgeHistory(nudgeHistoryPage + 1)}
                        disabled={nudgeHistoryPage >= Math.ceil(nudgeHistoryTotal / 25)}
                        className="btn"
                        style={{ opacity: nudgeHistoryPage >= Math.ceil(nudgeHistoryTotal / 25) ? 0.4 : 1 }}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔔</div>
                  <div style={{ fontSize: '14px' }}>No nudge history found</div>
                </div>
              )}
            </Widget>
          </section>
        </>
      )}

      {/* Learner Detail Modal */}
      {learnerDetail && (
        <>
          <div
            onClick={() => setLearnerDetail(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 style={{ margin: 0 }}>Learner Details</h2>
                <button
                  onClick={() => setLearnerDetail(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: 'var(--text-muted)'
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '4px' }}>
                  {learnerDetail.learner.name}
                </div>
                <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                  {learnerDetail.learner.email}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div className="card">
                  <div className="muted tiny">Total Nudges Received</div>
                  <div style={{ fontSize: '24px', fontWeight: 600 }}>{learnerDetail.stats.totalNudgesReceived}</div>
                </div>
                <div className="card">
                  <div className="muted tiny">Open Rate</div>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--status-success)' }}>{learnerDetail.stats.openRate}</div>
                </div>
                <div className="card">
                  <div className="muted tiny">Response Rate</div>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--brand-primary)' }}>{learnerDetail.stats.responseRate}</div>
                </div>
                <div className="card">
                  <div className="muted tiny">Last Nudge</div>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>
                    {learnerDetail.stats.lastNudgeAt ? new Date(learnerDetail.stats.lastNudgeAt).toLocaleDateString() : 'Never'}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Nudge Preferences</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{learnerDetail.preferences.emailEnabled ? '✅' : '❌'}</span>
                    <span>Email</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{learnerDetail.preferences.smsEnabled ? '✅' : '❌'}</span>
                    <span>SMS</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{learnerDetail.preferences.whatsappEnabled ? '✅' : '❌'}</span>
                    <span>WhatsApp</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{learnerDetail.preferences.inAppEnabled ? '✅' : '❌'}</span>
                    <span>In-App</span>
                  </div>
                </div>
                <div style={{ marginTop: '12px', fontSize: '14px', color: 'var(--text-muted)' }}>
                  <div>Frequency: <strong style={{ textTransform: 'capitalize' }}>{learnerDetail.preferences.frequency}</strong></div>
                  {learnerDetail.preferences.quietHoursStart && (
                    <div>Quiet Hours: <strong>{learnerDetail.preferences.quietHoursStart} - {learnerDetail.preferences.quietHoursEnd}</strong></div>
                  )}
                </div>
              </div>

              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '12px' }}>Recent Nudges</h3>
                <div style={{ maxHeight: '200px', overflow: 'auto' }}>
                  {learnerDetail.recentNudges.map((nudge: any, i: number) => (
                    <div
                      key={i}
                      style={{
                        padding: '12px',
                        background: 'var(--surface-page)',
                        borderRadius: '8px',
                        marginBottom: '8px',
                        fontSize: '13px'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <strong>{nudge.channel.toUpperCase()}</strong>
                        <span style={{ color: 'var(--text-muted)' }}>
                          {new Date(nudge.sentAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div style={{ color: 'var(--text-muted)', marginBottom: '4px' }}>{nudge.subject || nudge.message.substring(0, 50)}...</div>
                      <div style={{ display: 'flex', gap: '8px', fontSize: '11px' }}>
                        {nudge.opened && <span>👁️ Opened</span>}
                        {nudge.clicked && <span>🖱️ Clicked</span>}
                        {nudge.actionTaken && <span>✅ Action Taken</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Manual Nudge Slide-out Panel */}
      {manualNudgePanel.isOpen && (
        <>
          {/* Overlay */}
          <div
            onClick={closeManualNudgePanel}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
              animation: 'fadeIn 0.2s ease-in'
            }}
          />

          {/* Slide-out Panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '450px',
              background: 'var(--bg)',
              boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              animation: 'slideInRight 0.3s ease-out'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Send Manual Nudge</h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--muted)' }}>
                  To: {manualNudgePanel.learner?.name}
                  {manualNudgePanel.course && ` • ${manualNudgePanel.course}`}
                </p>
              </div>
              <button
                onClick={closeManualNudgePanel}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  padding: 0,
                  lineHeight: 1
                }}
                title="Close"
              >
                ×
              </button>
            </div>

            {/* Form */}
            <div style={{
              flex: 1,
              padding: '24px',
              overflowY: 'auto'
            }}>
              {/* Channel Selector */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '8px',
                  color: 'var(--fg)'
                }}>
                  Channel
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {(['email', 'sms', 'whatsapp', 'in_app'] as const).map(channel => (
                    <button
                      key={channel}
                      onClick={() => setManualNudgeForm({ ...manualNudgeForm, channel })}
                      style={{
                        flex: 1,
                        padding: '10px',
                        border: `2px solid ${manualNudgeForm.channel === channel ? 'var(--accent)' : 'var(--border)'}`,
                        background: manualNudgeForm.channel === channel ? 'var(--accent-light)' : 'transparent',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        transition: 'all 0.2s'
                      }}
                    >
                      {channel === 'in_app' ? 'In-App' : channel}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject (Email only) */}
              {manualNudgeForm.channel === 'email' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    color: 'var(--fg)'
                  }}>
                    Subject *
                  </label>
                  <input
                    type="text"
                    value={manualNudgeForm.subject}
                    onChange={(e) => setManualNudgeForm({ ...manualNudgeForm, subject: e.target.value })}
                    placeholder="Enter email subject..."
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      fontSize: '14px',
                      background: 'var(--bg)',
                      color: 'var(--fg)'
                    }}
                  />
                </div>
              )}

              {/* Message */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '8px',
                  color: 'var(--fg)'
                }}>
                  Message *
                </label>
                <textarea
                  value={manualNudgeForm.message}
                  onChange={(e) => setManualNudgeForm({ ...manualNudgeForm, message: e.target.value })}
                  placeholder="Enter your message..."
                  rows={8}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: 'var(--bg)',
                    color: 'var(--fg)',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
                <div style={{
                  marginTop: '6px',
                  fontSize: '12px',
                  color: 'var(--muted)'
                }}>
                  {manualNudgeForm.message.length} characters
                </div>
              </div>

              {/* Message Templates */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  marginBottom: '8px',
                  color: 'var(--fg)'
                }}>
                  Quick Templates
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <button
                    onClick={() => setManualNudgeForm({
                      ...manualNudgeForm,
                      subject: 'We miss you in your course!',
                      message: `Hi ${manualNudgePanel.learner?.name || 'there'},\n\nWe noticed you haven't been active in ${manualNudgePanel.course || 'your course'} recently. Your learning journey is important to us!\n\nLog back in and continue where you left off.`
                    })}
                    className="btn tiny"
                    style={{ justifyContent: 'flex-start', fontSize: '12px' }}
                  >
                    📚 Re-engagement
                  </button>
                  <button
                    onClick={() => setManualNudgeForm({
                      ...manualNudgeForm,
                      subject: 'Course deadline approaching',
                      message: `Hi ${manualNudgePanel.learner?.name || 'there'},\n\nYour ${manualNudgePanel.course || 'course'} deadline is approaching soon. Make sure to complete all pending activities.\n\nNeed help? Reach out to your instructor.`
                    })}
                    className="btn tiny"
                    style={{ justifyContent: 'flex-start', fontSize: '12px' }}
                  >
                    ⏰ Deadline Reminder
                  </button>
                  <button
                    onClick={() => setManualNudgeForm({
                      ...manualNudgeForm,
                      subject: 'Great progress - keep it up!',
                      message: `Hi ${manualNudgePanel.learner?.name || 'there'},\n\nYou're making great progress in ${manualNudgePanel.course || 'your course'}! Keep up the excellent work.\n\nYou're almost there - don't stop now!`
                    })}
                    className="btn tiny"
                    style={{ justifyContent: 'flex-start', fontSize: '12px' }}
                  >
                    ✨ Encouragement
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeManualNudgePanel}
                className="btn"
                disabled={sendingNudge}
                style={{
                  padding: '10px 20px',
                  opacity: sendingNudge ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={sendManualNudge}
                disabled={sendingNudge || !manualNudgeForm.message.trim() || (manualNudgeForm.channel === 'email' && !manualNudgeForm.subject.trim())}
                style={{
                  padding: '10px 24px',
                  background: 'var(--accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: 600,
                  cursor: sendingNudge ? 'wait' : 'pointer',
                  opacity: (sendingNudge || !manualNudgeForm.message.trim() || (manualNudgeForm.channel === 'email' && !manualNudgeForm.subject.trim())) ? 0.5 : 1,
                  fontSize: '14px'
                }}
              >
                {sendingNudge ? 'Sending...' : 'Send Nudge'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* New Rule Modal */}
      {newRuleModal && (
        <>
          {/* Overlay */}
          <div
            onClick={() => setNewRuleModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999,
              animation: 'fadeIn 0.2s ease-in'
            }}
          />

          {/* Modal */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'var(--bg)',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              zIndex: 1000,
              width: '600px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              animation: 'fadeIn 0.2s ease-in'
            }}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Create New Nudge Rule</h2>
              <button
                onClick={() => setNewRuleModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  padding: 0,
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Name */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>
                    Rule Name <span style={{ color: 'var(--status-danger)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={newRuleForm.name}
                    onChange={(e) => setNewRuleForm({ ...newRuleForm, name: e.target.value })}
                    placeholder="e.g., Weekend Inactive Alert"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Description */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>
                    Description
                  </label>
                  <textarea
                    value={newRuleForm.description}
                    onChange={(e) => setNewRuleForm({ ...newRuleForm, description: e.target.value })}
                    placeholder="Describe what this rule does..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '14px',
                      resize: 'vertical'
                    }}
                  />
                </div>

                {/* Category */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>
                    Category <span style={{ color: 'var(--status-danger)' }}>*</span>
                  </label>
                  <select
                    value={newRuleForm.category}
                    onChange={(e) => setNewRuleForm({ ...newRuleForm, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="engagement">Engagement</option>
                    <option value="deadline">Deadline</option>
                    <option value="progress">Progress</option>
                    <option value="performance">Performance</option>
                    <option value="competency">Competency</option>
                  </select>
                </div>

                {/* Trigger Conditions */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>
                    Trigger Conditions <span style={{ color: 'var(--status-danger)' }}>*</span>
                  </label>
                  {newRuleForm.triggerConditions.map((tc, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={tc.field}
                        onChange={(e) => {
                          const updated = [...newRuleForm.triggerConditions];
                          updated[idx].field = e.target.value;
                          setNewRuleForm({ ...newRuleForm, triggerConditions: updated });
                        }}
                        placeholder="Field (e.g., progress)"
                        style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '13px' }}
                      />
                      <select
                        value={tc.operator}
                        onChange={(e) => {
                          const updated = [...newRuleForm.triggerConditions];
                          updated[idx].operator = e.target.value;
                          setNewRuleForm({ ...newRuleForm, triggerConditions: updated });
                        }}
                        style={{ width: '80px', padding: '8px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '13px' }}
                      >
                        <option value="">Op</option>
                        <option value="min">≥</option>
                        <option value="max">≤</option>
                        <option value="lt">&lt;</option>
                        <option value="gt">&gt;</option>
                        <option value="lte">≤</option>
                        <option value="gte">≥</option>
                      </select>
                      <input
                        type="text"
                        value={tc.value}
                        onChange={(e) => {
                          const updated = [...newRuleForm.triggerConditions];
                          updated[idx].value = e.target.value;
                          setNewRuleForm({ ...newRuleForm, triggerConditions: updated });
                        }}
                        placeholder="Value"
                        style={{ width: '80px', padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '13px' }}
                      />
                      {idx > 0 && (
                        <button
                          onClick={() => {
                            const updated = newRuleForm.triggerConditions.filter((_, i) => i !== idx);
                            setNewRuleForm({ ...newRuleForm, triggerConditions: updated });
                          }}
                          style={{
                            padding: '8px 12px',
                            background: 'var(--status-danger)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          −
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setNewRuleForm({
                      ...newRuleForm,
                      triggerConditions: [...newRuleForm.triggerConditions, { field: '', operator: '', value: '' }]
                    })}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      background: 'var(--border)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    + Add Condition
                  </button>
                  <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--muted)' }}>
                    Common fields: daysSinceLastLogin, progress, riskScore, daysUntilDeadline, loginFrequency, quizScore
                  </div>
                </div>

                {/* Channels */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>
                    Channels <span style={{ color: 'var(--status-danger)' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    {['email', 'sms', 'whatsapp', 'in_app'].map(ch => (
                      <label key={ch} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={newRuleForm.channels.includes(ch)}
                          onChange={(e) => {
                            const updated = e.target.checked
                              ? [...newRuleForm.channels, ch]
                              : newRuleForm.channels.filter(c => c !== ch);
                            setNewRuleForm({ ...newRuleForm, channels: updated });
                          }}
                        />
                        <span style={{ fontSize: '13px', textTransform: 'capitalize' }}>{ch.replace('_', '-')}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>
                    Priority
                  </label>
                  <select
                    value={newRuleForm.priority}
                    onChange={(e) => setNewRuleForm({ ...newRuleForm, priority: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                {/* Frequency Caps */}
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>
                      Max Per Day
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newRuleForm.maxPerDay}
                      onChange={(e) => setNewRuleForm({ ...newRuleForm, maxPerDay: parseInt(e.target.value) || 1 })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500 }}>
                      Max Per Week
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={newRuleForm.maxPerWeek}
                      onChange={(e) => setNewRuleForm({ ...newRuleForm, maxPerWeek: parseInt(e.target.value) || 1 })}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid var(--border)',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setNewRuleModal(false)}
                disabled={creatingRule}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createNudgeRule}
                disabled={creatingRule || !newRuleForm.name || newRuleForm.channels.length === 0}
                style={{
                  padding: '10px 20px',
                  background: 'var(--brand-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: creatingRule || !newRuleForm.name || newRuleForm.channels.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  opacity: creatingRule || !newRuleForm.name || newRuleForm.channels.length === 0 ? 0.5 : 1
                }}
              >
                {creatingRule ? 'Creating...' : 'Create Rule'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* ENGAGEMENT ANALYTICS TAB */}
      {activeTab === 'engagement' && (
        <>
          {/* View Toggle */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '24px',
            padding: '4px',
            background: 'var(--surface-sunken)',
            borderRadius: '8px',
            width: 'fit-content'
          }}>
            <button
              onClick={() => {
                setEngagementView('learner');
                setDisplayedLearnerCount(50);
              }}
              style={{
                padding: '10px 24px',
                background: engagementView === 'learner' ? 'white' : 'transparent',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: engagementView === 'learner' ? 600 : 400,
                color: engagementView === 'learner' ? '#1e293b' : '#64748b',
                boxShadow: engagementView === 'learner' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              Learner-Centric View
            </button>
            <button
              onClick={() => {
                setEngagementView('resource');
                setDisplayedResourceCount(50);
              }}
              style={{
                padding: '10px 24px',
                background: engagementView === 'resource' ? 'white' : 'transparent',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: engagementView === 'resource' ? 600 : 400,
                color: engagementView === 'resource' ? '#1e293b' : '#64748b',
                boxShadow: engagementView === 'resource' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              Content-Centric View
            </button>
          </div>

          {/* Loading State */}
          {engagementLoading && (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--muted)' }}>
              Loading engagement data...
            </div>
          )}

          {/* Learner-Centric View */}
          {!engagementLoading && engagementView === 'learner' && (
            <section className="grid g-1">
              <Widget
                id="learner-engagement"
                title={<strong>Learner Engagement Summary</strong>}
              >
                {learnerEngagementData.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)' }}>
                    No learner engagement data available
                  </div>
                ) : (
                  <div
                    style={{
                      maxHeight: '600px',
                      overflowY: 'auto',
                      overflowX: 'auto',
                      position: 'relative'
                    }}
                    onScroll={(e) => {
                      const target = e.target as HTMLDivElement;
                      const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;

                      if (scrolledToBottom && !isLoadingMore && displayedLearnerCount < learnerEngagementData.length) {
                        setIsLoadingMore(true);
                        setTimeout(() => {
                          setDisplayedLearnerCount(prev => Math.min(prev + 50, learnerEngagementData.length));
                          setIsLoadingMore(false);
                        }, 300);
                      }
                    }}
                  >
                    <table>
                      <thead style={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                        zIndex: 10,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <tr>
                          <th style={{ textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>Learner</th>
                          <th style={{ textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>Program</th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Activities
                            <InfoIcon tooltip="Total number of learning activities (views, interactions) recorded for this learner in this program." />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Time Spent (min)
                            <InfoIcon tooltip="Total time spent by the learner on learning activities in this program. Tracked from learning_analytics data." />
                          </th>
                          <th style={{ textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Last Active
                            <InfoIcon tooltip="Most recent date when the learner had any activity in this program." />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Engagement Score
                            <InfoIcon tooltip={`Engagement Score (0-100) is calculated as:\n\n• Activity Frequency: Up to 40 points\n  (2 points per activity, capped at 40)\n\n• Recency: Up to 40 points\n  - ≤1 day ago: 40 points\n  - ≤3 days ago: 30 points\n  - ≤7 days ago: 20 points\n  - ≤14 days ago: 10 points\n\n• Time Spent: Up to 20 points\n  (1 point per 30 minutes, capped at 20)\n\nNote: Only learners with incomplete coursework are shown. Learners who have completed the module are excluded.`} />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Status
                            <InfoIcon tooltip={`Engagement Status:\n\n• Active (Green): Last activity within 7 days\n• Declining (Orange): Last activity 8-14 days ago\n• Inactive (Red): Last activity >14 days ago\n\nNote: Only shows learners with INCOMPLETE modules. Learners who completed their coursework are excluded from this view.`} />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Trend
                            <InfoIcon tooltip={`Engagement Trend:\n\n📈 Up: Score ≥70 (strong engagement)\n➡️ Stable: Score 50-69 (moderate engagement)\n📉 Down: Score <50 (weak engagement)`} />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {learnerEngagementData.slice(0, displayedLearnerCount).map((learner: any, idx: number) => (
                          <tr key={idx}>
                            <td style={{ textAlign: 'left' }}>{learner.learnerName}</td>
                            <td style={{ textAlign: 'left', fontSize: '13px', color: 'var(--muted)' }}>
                              {learner.programName}
                            </td>
                            <td className="right">{learner.totalActivities}</td>
                            <td className="right">{learner.totalTimeSpent}</td>
                            <td style={{ textAlign: 'left', fontSize: '13px' }}>
                              {learner.lastActive
                                ? new Date(learner.lastActive).toLocaleDateString()
                                : 'Never'}
                            </td>
                            <td className="right">
                              <span style={{
                                fontWeight: 600,
                                color: learner.engagementScore >= 70 ? '#10b981' : learner.engagementScore >= 40 ? '#f59e0b' : '#ef4444'
                              }}>
                                {learner.engagementScore}
                              </span>
                            </td>
                            <td className="right">
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 500,
                                background: learner.status === 'active' ? '#d1fae5' : learner.status === 'declining' ? '#fed7aa' : '#fee2e2',
                                color: learner.status === 'active' ? '#065f46' : learner.status === 'declining' ? '#9a3412' : '#991b1b'
                              }}>
                                {learner.status}
                              </span>
                            </td>
                            <td className="right">
                              <span style={{ fontSize: '18px' }}>
                                {learner.trend === 'up' ? '📈' : learner.trend === 'down' ? '📉' : '➡️'}
                              </span>
                            </td>
                            <td className="right">
                              <button
                                onClick={() => fetchLearnerActivityDetail(learner.userId, learner.programId)}
                                style={{
                                  padding: '4px 12px',
                                  fontSize: '12px',
                                  background: 'var(--brand-primary)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {isLoadingMore && (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>
                        Loading more...
                      </div>
                    )}
                    {displayedLearnerCount >= learnerEngagementData.length && learnerEngagementData.length > 50 && (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)', fontSize: '14px' }}>
                        Showing all {learnerEngagementData.length} learners
                      </div>
                    )}
                  </div>
                )}
              </Widget>
            </section>
          )}

          {/* Content-Centric View */}
          {!engagementLoading && engagementView === 'resource' && (
            <section className="grid g-1">
              <Widget
                id="resource-usage"
                title={<strong>Resource Usage Summary</strong>}
              >
                {resourceUsageData.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)' }}>
                    No resource usage data available
                  </div>
                ) : (
                  <div
                    style={{
                      maxHeight: '600px',
                      overflowY: 'auto',
                      overflowX: 'auto',
                      position: 'relative'
                    }}
                    onScroll={(e) => {
                      const target = e.target as HTMLDivElement;
                      const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;

                      if (scrolledToBottom && !isLoadingMore && displayedResourceCount < resourceUsageData.length) {
                        setIsLoadingMore(true);
                        setTimeout(() => {
                          setDisplayedResourceCount(prev => Math.min(prev + 50, resourceUsageData.length));
                          setIsLoadingMore(false);
                        }, 300);
                      }
                    }}
                  >
                    <table>
                      <thead style={{
                        position: 'sticky',
                        top: 0,
                        backgroundColor: 'white',
                        zIndex: 10,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <tr>
                          <th style={{ textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Resource
                            <InfoIcon tooltip="Name of the learning resource (video, document, quiz, etc.). Resource ID is shown in parentheses for reference." />
                          </th>
                          <th style={{ textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Type
                            <InfoIcon tooltip="Type of learning resource. Automatically detected from resource name: Video, Quiz, Document, or Other." />
                          </th>
                          <th style={{ textAlign: 'left', position: 'sticky', top: 0, backgroundColor: 'white' }}>Program</th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Total Views
                            <InfoIcon tooltip="Total number of times this resource has been accessed by all learners. Multiple views by the same learner are counted." />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Unique Learners
                            <InfoIcon tooltip="Number of distinct learners who have accessed this resource at least once." />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Avg Time (min)
                            <InfoIcon tooltip="Average time spent per learner on this resource, calculated from learning_analytics TimeSpent data." />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Completion %
                            <InfoIcon tooltip={`Percentage of learners who completed this resource.\n\nCompletion is determined by:\n• learning_analytics.Verb = 'completed' for videos/documents\n• user_study_resource_submission.IsPassed = 1 for quizzes/assignments\n\nNote: This shows completion rate regardless of when completion occurred. Learners who completed the resource weeks ago are counted as completed.`} />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>
                            Effectiveness
                            <InfoIcon tooltip={`Resource Effectiveness Score (1-5 stars):\n\n⭐⭐ Completion Rate (0-2 stars)\n  Based on % of learners who completed\n\n⭐⭐ Engagement (0-2 stars)\n  Based on views per learner\n  (Higher = more revisits)\n\n⭐ Revisit Value (0-1 star)\n  Based on learners who returned\n  after first view\n\nHigher scores indicate more effective learning resources that engage learners and drive completion.`} />
                          </th>
                          <th className="right" style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resourceUsageData.slice(0, displayedResourceCount).map((resource: any, idx: number) => (
                          <tr key={idx}>
                            <td style={{ textAlign: 'left' }}>{resource.resourceName}</td>
                            <td style={{ textAlign: 'left', fontSize: '13px' }}>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '4px',
                                background: 'var(--surface-sunken)',
                                fontWeight: 500
                              }}>
                                {resource.resourceType}
                              </span>
                            </td>
                            <td style={{ textAlign: 'left', fontSize: '13px', color: 'var(--muted)' }}>
                              {resource.programName}
                            </td>
                            <td className="right">{resource.totalViews}</td>
                            <td className="right">{resource.uniqueLearners}</td>
                            <td className="right">{resource.avgTimeSpent}</td>
                            <td className="right">
                              <span style={{
                                fontWeight: 600,
                                color: resource.completionRate >= 70 ? '#10b981' : resource.completionRate >= 40 ? '#f59e0b' : '#ef4444'
                              }}>
                                {resource.completionRate}%
                              </span>
                            </td>
                            <td className="right">
                              <span style={{ color: 'var(--status-warning)', fontSize: '16px' }}>
                                {'⭐'.repeat(Math.round(resource.effectiveness))}
                                {'☆'.repeat(5 - Math.round(resource.effectiveness))}
                              </span>
                            </td>
                            <td className="right">
                              <button
                                onClick={() => fetchResourceActivityDetail(resource.resourceId, resource.programId)}
                                style={{
                                  padding: '4px 12px',
                                  fontSize: '12px',
                                  background: 'var(--brand-primary)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {isLoadingMore && (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)' }}>
                        Loading more...
                      </div>
                    )}
                    {displayedResourceCount >= resourceUsageData.length && resourceUsageData.length > 50 && (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'var(--muted)', fontSize: '14px' }}>
                        Showing all {resourceUsageData.length} resources
                      </div>
                    )}
                  </div>
                )}
              </Widget>
            </section>
          )}

          {/* Learner Detail Drill-Down Modal */}
          {selectedLearnerDetail && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setSelectedLearnerDetail(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '900px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
                }}
              >
                {/* Header */}
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: 600 }}>
                      {selectedLearnerDetail.learner.name}
                    </h3>
                    <p style={{ margin: 0, color: 'var(--muted)', fontSize: '14px' }}>
                      {selectedLearnerDetail.learner.programName}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedLearnerDetail(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: 'var(--muted)'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Summary Stats */}
                <div style={{ padding: '24px', borderBottom: '1px solid var(--border)' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Summary</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Total Activities</div>
                      <div style={{ fontSize: '24px', fontWeight: 600 }}>
                        {selectedLearnerDetail.summary.totalActivities}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Time Spent</div>
                      <div style={{ fontSize: '24px', fontWeight: 600 }}>
                        {selectedLearnerDetail.summary.totalTimeSpent} min
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Avg Session</div>
                      <div style={{ fontSize: '24px', fontWeight: 600 }}>
                        {selectedLearnerDetail.summary.avgSessionLength} min
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Total Sessions</div>
                      <div style={{ fontSize: '24px', fontWeight: 600 }}>
                        {selectedLearnerDetail.summary.totalSessions}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity Breakdown */}
                <div style={{ padding: '24px', borderBottom: '1px solid var(--border)' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Activity Breakdown</h4>
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Resource</th>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Type</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Views</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Time (min)</th>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Last Accessed</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedLearnerDetail.activities.map((activity: any, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={{ textAlign: 'left', padding: '8px' }}>{activity.resourceName}</td>
                          <td style={{ textAlign: 'left', padding: '8px', fontSize: '13px', color: 'var(--muted)' }}>
                            {activity.resourceType}
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px' }}>{activity.views}</td>
                          <td style={{ textAlign: 'right', padding: '8px' }}>{activity.timeSpent}</td>
                          <td style={{ textAlign: 'left', padding: '8px', fontSize: '13px' }}>
                            {activity.lastAccessed
                              ? new Date(activity.lastAccessed).toLocaleDateString()
                              : 'Never'}
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: activity.status === 'completed' ? '#d1fae5' : activity.status === 'in_progress' ? '#fef3c7' : '#f1f5f9',
                              color: activity.status === 'completed' ? '#065f46' : activity.status === 'in_progress' ? '#92400e' : '#64748b'
                            }}>
                              {activity.status.replace('_', ' ')}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Patterns */}
                <div style={{ padding: '24px' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Learning Patterns</h4>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: 500 }}>Navigation Style:</span>
                      <span style={{
                        padding: '2px 12px',
                        borderRadius: '4px',
                        background: 'var(--surface-sunken)',
                        fontSize: '13px'
                      }}>
                        {selectedLearnerDetail.patterns.navigationStyle}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: 500 }}>Revisit Rate:</span>
                      <span>{selectedLearnerDetail.patterns.revisitRate}%</span>
                    </div>
                    <div>
                      <span style={{ fontWeight: 500 }}>Peak Activity Times:</span>
                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {selectedLearnerDetail.patterns.peakActivityTimes.map((time: string, idx: number) => (
                          <span
                            key={idx}
                            style={{
                              padding: '4px 12px',
                              borderRadius: '4px',
                              background: 'var(--status-info-bg)',
                              color: 'var(--brand-primary-active)',
                              fontSize: '13px'
                            }}
                          >
                            {time}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resource Detail Drill-Down Modal */}
          {selectedResourceDetail && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setSelectedResourceDetail(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  maxWidth: '900px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflow: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
                }}
              >
                {/* Header */}
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: 600 }}>
                      {selectedResourceDetail.resource.name}
                    </h3>
                    <p style={{ margin: 0, color: 'var(--muted)', fontSize: '14px' }}>
                      {selectedResourceDetail.resource.type} • {selectedResourceDetail.resource.programName}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedResourceDetail(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: 'var(--muted)'
                    }}
                  >
                    ×
                  </button>
                </div>

                {/* Summary Stats */}
                <div style={{ padding: '24px', borderBottom: '1px solid var(--border)' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Summary</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Total Views</div>
                      <div style={{ fontSize: '24px', fontWeight: 600 }}>
                        {selectedResourceDetail.summary.totalViews}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Unique Learners</div>
                      <div style={{ fontSize: '24px', fontWeight: 600 }}>
                        {selectedResourceDetail.summary.uniqueLearners}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Avg Time</div>
                      <div style={{ fontSize: '24px', fontWeight: 600 }}>
                        {selectedResourceDetail.summary.avgTimeSpent} min
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Completion</div>
                      <div style={{ fontSize: '24px', fontWeight: 600, color: 'var(--status-success)' }}>
                        {selectedResourceDetail.summary.completionRate}%
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Revisit Rate</div>
                      <div style={{ fontSize: '24px', fontWeight: 600 }}>
                        {selectedResourceDetail.summary.revisitRate}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Learners List */}
                <div style={{ padding: '24px', borderBottom: '1px solid var(--border)' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Learners</h4>
                  <table style={{ width: '100%' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Name</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Views</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Time (min)</th>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Last Accessed</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedResourceDetail.learners.map((learner: any, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={{ textAlign: 'left', padding: '8px' }}>{learner.learnerName}</td>
                          <td style={{ textAlign: 'right', padding: '8px' }}>{learner.views}</td>
                          <td style={{ textAlign: 'right', padding: '8px' }}>{learner.timeSpent}</td>
                          <td style={{ textAlign: 'left', padding: '8px', fontSize: '13px' }}>
                            {learner.lastAccessed
                              ? new Date(learner.lastAccessed).toLocaleDateString()
                              : 'Never'}
                          </td>
                          <td style={{ textAlign: 'right', padding: '8px' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              background: learner.status === 'completed' ? '#d1fae5' : '#fef3c7',
                              color: learner.status === 'completed' ? '#065f46' : '#92400e'
                            }}>
                              {learner.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Trends */}
                <div style={{ padding: '24px' }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Usage Patterns</h4>
                  <div>
                    <span style={{ fontWeight: 500 }}>Peak Usage Times:</span>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {selectedResourceDetail.trends.peakUsageTimes.map((time: string, idx: number) => (
                        <span
                          key={idx}
                          style={{
                            padding: '4px 12px',
                            borderRadius: '4px',
                            background: 'var(--status-info-bg)',
                            color: 'var(--brand-primary-active)',
                            fontSize: '13px'
                          }}
                        >
                          {time}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Data Source Modal */}
      <DataSourceModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        title={modalState.title}
        dataSource={modalState.source}
        records={modalState.records}
        explanation={modalState.explanation}
        apiUrl={modalState.apiUrl}
      />
    </div>
  );
};

export default LmsAnalytics;
